import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { BrowserRouter, Routes, Route, useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import rehypeRaw from 'rehype-raw';
import 'katex/dist/katex.min.css';
import { UploadCloud, FileText, Check, X, Pencil, Save, ChevronLeft, ChevronRight, Download, BrainCircuit, Loader2, Database, FileClock, Info, BookOpen, Globe, Eye, Trash2, ArrowLeft, RefreshCw, DollarSign, FileSearch, Languages, BarChart3, Copy, CheckCircle2 } from 'lucide-react';

// --- Configuration ---
// API_URL can be configured via environment variable REACT_APP_API_URL
// For Vercel: Set REACT_APP_API_URL in Environment Variables
// Defaults to localhost for local development
const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8888";

// --- Main App Component ---
export default function App() {
  const [view, setView] = useState('upload'); // 'upload', 'review', 'documents', 'docs'
  const [documents, setDocuments] = useState([]);
  const [token, setToken] = useState(() => localStorage.getItem('access_token') || '');
  const [user, setUser] = useState(null);
  const [selectedDocId, setSelectedDocId] = useState(null);

  // Attach token to axios
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  const fetchMe = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API_URL}/auth/me`);
      setUser(res.data);
    } catch (e) {
      console.error('Auth check failed:', e);
      setUser(null);
      setToken('');
      localStorage.removeItem('access_token');
    }
  }, [token]);

  const fetchDocuments = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/documents`);
      setDocuments(response.data);
    } catch (error) {
      console.error("Error fetching documents:", error);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  useEffect(() => {
    if (token) {
      fetchDocuments();
    }
  }, [token, fetchDocuments]);

  return (
    <BrowserRouter>
      <div className="min-h-screen bg-gray-100 font-sans text-gray-900">
        <Header user={user} onLogout={() => {
          setUser(null);
          setToken('');
          localStorage.removeItem('access_token');
        }} />
        <main className="w-full p-2 md:p-4">
          <div className="bg-white shadow-xl rounded-lg overflow-hidden">
            {!user ? (
              <LoginView onLoginSuccess={(tok, me) => { setToken(tok); localStorage.setItem('access_token', tok); setUser(me); }} />
            ) : (
              <Routes>
                <Route path="/term/:termId" element={<TermDetailsView user={user} />} />
                <Route path="/documents/:documentId" element={<DocumentsViewWithRoute user={user} fetchDocuments={fetchDocuments} />} />
                <Route path="/documents" element={<DocumentsViewWithRoute user={user} fetchDocuments={fetchDocuments} />} />
                <Route path="/review" element={<ReviewViewWithRoute user={user} />} />
                <Route path="/documentation" element={<DocsViewWithRoute />} />
                <Route path="/" element={<UploadViewWithRoute user={user} fetchDocuments={fetchDocuments} />} />
                <Route path="*" element={
                  <MainView
                    view={view}
                    setView={setView}
                    selectedDocId={selectedDocId}
                    setSelectedDocId={setSelectedDocId}
                    fetchDocuments={fetchDocuments}
                    documents={documents}
                    user={user}
                  />
                } />
              </Routes>
            )}
          </div>
        </main>
        <Toaster />
      </div>
    </BrowserRouter>
  );
}

// --- Navigation ---
function MainView({ view, setView, selectedDocId, setSelectedDocId, fetchDocuments, documents, user }) {
  const navigate = useNavigate();

  return (
    <>
      <NavBar view={view} setView={setView} />
      {view === 'upload' && <UploadView onUploadSuccess={fetchDocuments} onDuplicate={(docId) => {
        setSelectedDocId(docId);
        setView('documents');
        navigate(`/documents/${docId}`);
      }} user={user} />}
      {view === 'review' && <ReviewView user={user} />}
      {view === 'documents' && <DocumentsView initialDocumentId={selectedDocId} user={user} />}
      {view === 'docs' && <DocsView />}
      {view !== 'docs' && <DocumentStatus documents={documents} fetchDocuments={fetchDocuments} />}
    </>
  );
}

function Header({ user, onLogout }) {
  const { t, i18n } = useTranslation();

  const handleLogout = () => {
    onLogout();
    showToast(t('toast.loggedOut'), 'success');
  };

  return (
    <header className="bg-white shadow-md">
      <div className="w-full px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <BrainCircuit className="h-8 w-8 text-blue-600" />
          <h1 className="text-2xl font-bold text-gray-800">
            {t('header.title')}
          </h1>
        </div>
        <div className="flex items-center space-x-4">
          {/* Language Switcher */}
          <div className="flex items-center space-x-2">
            <Globe className="h-4 w-4 text-gray-500" />
            <select
              value={i18n.language}
              onChange={(e) => i18n.changeLanguage(e.target.value)}
              className="px-2 py-1 border border-gray-300 rounded text-xs focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="en">English</option>
              <option value="zh">中文</option>
              <option value="vi">Tiếng Việt</option>
            </select>
          </div>
          {user ? (
            <div className="flex items-center space-x-3">
              <span className="text-sm text-gray-600">{user.username}</span>
              <button onClick={handleLogout} className="text-sm text-red-600 hover:text-red-800">{t('common.logout')}</button>
            </div>
          ) : (
            <span className="text-sm text-gray-500">{t('common.pleaseLogin')}</span>
          )}
        </div>
      </div>
    </header>
  );
}

function NavBar({ view, setView }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const commonButtonClass = "py-3 px-4 font-medium text-sm transition-all duration-200 ease-in-out flex items-center space-x-2";
  const activeClass = "border-b-4 border-blue-600 text-blue-600";
  const inactiveClass = "text-gray-500 hover:text-gray-800 hover:bg-gray-50";

  // Determine active view from URL
  const currentView = location.pathname.startsWith('/documents') ? 'documents' :
    location.pathname.startsWith('/review') ? 'review' :
      location.pathname.startsWith('/documentation') ? 'docs' :
        location.pathname === '/' ? 'upload' :
          location.pathname.startsWith('/term') ? view : view;

  const handleNavClick = (newView) => {
    // Navigate first, then update state
    if (newView === 'documents') {
      // Check if we're already on a documents route
      const match = location.pathname.match(/\/documents\/(\d+)/);
      if (match) {
        // Already on a document page, keep it
        const searchParams = new URLSearchParams(location.search);
        const page = searchParams.get('p');
        const url = page ? `/documents/${match[1]}?p=${page}` : `/documents/${match[1]}`;
        navigate(url);
      } else if (location.pathname.startsWith('/documents')) {
        // On /documents but no specific document, stay there
        navigate('/documents');
      } else {
        // Coming from another route, navigate to /documents
        navigate('/documents');
      }
      setView('documents');
    } else if (newView === 'review') {
      // Keep current review filters/sort/page in URL if already on review route
      if (location.pathname === '/review') {
        const searchParams = new URLSearchParams(location.search);
        const params = [];
        if (searchParams.get('p')) params.push(`p=${searchParams.get('p')}`);
        if (searchParams.get('filter')) params.push(`filter=${searchParams.get('filter')}`);
        if (searchParams.get('showDiscrepanciesOnly')) params.push(`showDiscrepanciesOnly=${searchParams.get('showDiscrepanciesOnly')}`);
        if (searchParams.get('sortByDiscrepancy')) params.push(`sortByDiscrepancy=${searchParams.get('sortByDiscrepancy')}`);
        if (searchParams.get('documentIds')) params.push(`documentIds=${searchParams.get('documentIds')}`);
        const query = params.length > 0 ? `?${params.join('&')}` : '';
        navigate(`/review${query}`);
      } else {
        // Navigate to review with default params (no query string)
        navigate('/review');
      }
      setView('review');
    } else if (newView === 'docs') {
      navigate('/documentation');
      setView('docs');
    } else if (newView === 'upload') {
      navigate('/');
      setView('upload');
    } else {
      navigate('/');
      setView(newView);
    }
  };

  return (
    <nav className="flex border-b border-gray-200">
      <button
        onClick={() => handleNavClick('upload')}
        className={`${commonButtonClass} ${currentView === 'upload' ? activeClass : inactiveClass}`}
      >
        <UploadCloud className="h-5 w-5" />
        <span>{t('nav.uploadDocuments')}</span>
      </button>
      <button
        onClick={() => handleNavClick('review')}
        className={`${commonButtonClass} ${currentView === 'review' ? activeClass : inactiveClass}`}
      >
        <Pencil className="h-5 w-5" />
        <span>{t('nav.reviewTranslations')}</span>
      </button>
      <button
        onClick={() => handleNavClick('documents')}
        className={`${commonButtonClass} ${currentView === 'documents' ? activeClass : inactiveClass}`}
      >
        <Database className="h-5 w-5" />
        <span>{t('nav.documents')}</span>
      </button>
      <button
        onClick={() => handleNavClick('docs')}
        className={`${commonButtonClass} ${currentView === 'docs' ? activeClass : inactiveClass}`}
      >
        <BookOpen className="h-5 w-5" />
        <span>{t('nav.documentation')}</span>
      </button>
    </nav>
  );
}

// --- Login View ---
function LoginView({ onLoginSuccess }) {
  const { t } = useTranslation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const body = new URLSearchParams();
      body.append('username', username);
      body.append('password', password);
      const res = await axios.post(`${API_URL}/auth/login`, body, { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } });
      const token = res.data.access_token;
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const me = await axios.get(`${API_URL}/auth/me`);
      onLoginSuccess(token, me.data);
      showToast(t('login.loggedInSuccess'), 'success');
    } catch (err) {
      console.error('Login failed', err);
      showToast(t('login.loginFailed'), 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 md:p-10">
      <h2 className="text-xl font-semibold text-gray-700 mb-6">{t('login.title')}</h2>
      <form onSubmit={submit} className="max-w-sm space-y-4">
        <input type="text" value={username} onChange={(e) => setUsername(e.target.value)} placeholder={t('common.username')} className="w-full p-2 border rounded" />
        <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t('common.password')} className="w-full p-2 border rounded" />
        <button type="submit" disabled={loading} className="px-4 py-2 bg-blue-600 text-white rounded disabled:opacity-50">{loading ? t('login.loggingIn') : t('common.login')}</button>
      </form>
      <p className="text-sm text-gray-500 mt-4">{t('login.noAccount')}</p>
    </div>
  );
}

// --- Upload View Component ---
// Wrapper component for route-based UploadView
function UploadViewWithRoute({ user, fetchDocuments }) {
  const navigate = useNavigate();

  return (
    <>
      <NavBar view="upload" setView={() => { }} />
      <UploadView
        onUploadSuccess={fetchDocuments}
        onDuplicate={(docId) => {
          navigate(`/documents/${docId}`);
        }}
        user={user}
      />
      <DocumentStatus documents={[]} fetchDocuments={fetchDocuments} />
    </>
  );
}

function UploadView({ onUploadSuccess, onDuplicate, user }) {
  const { t } = useTranslation();
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const isViewOnly = user?.role === 'viewer';

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setUploadProgress(0);
  };

  const handleUpload = async () => {
    if (!file) {
      showToast(t('upload.selectFile'), "error");
      return;
    }
    setIsUploading(true);

    const formData = new FormData();
    formData.append("file", file);

    try {
      await axios.post(`${API_URL}/upload`, formData, {
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(percentCompleted);
        },
      });
      showToast(t('upload.uploadSuccess'), "success");
      setFile(null);
      onUploadSuccess(); // Refresh the document list
    } catch (error) {
      console.error("Error uploading file:", error);
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;
      if (status === 409 && detail?.document_id) {
        showToast(t('upload.duplicateDocument'), "error");
        if (onDuplicate) onDuplicate(detail.document_id);
      } else {
        showToast(`${t('upload.uploadFailed')} ${detail?.message || detail || error.message}`, "error");
      }
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  return (
    <div className="p-6 md:p-10">
      <h2 className="text-xl font-semibold text-gray-700 mb-6">{t('upload.title')}</h2>
      {isViewOnly && (
        <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
          <p className="text-sm text-yellow-800">{t('review.viewOnlyUpload', 'View-only users cannot upload documents')}</p>
        </div>
      )}
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center bg-gray-50">
        <input
          type="file"
          id="file-upload"
          className="hidden"
          onChange={handleFileChange}
          accept=".pdf,.txt"
          disabled={isUploading || isViewOnly}
        />
        {!file && !isUploading && (
          <label
            htmlFor="file-upload"
            className="cursor-pointer flex flex-col items-center text-gray-500 hover:text-blue-600 transition-colors"
          >
            <UploadCloud className="h-12 w-12 mb-2" />
            <span className="font-medium">{t('upload.dragDrop')}</span>
            <span className="text-sm">{t('upload.fileTypes')}</span>
          </label>
        )}

        {file && !isUploading && (
          <div className="text-left">
            <div className="flex items-center space-x-3 p-3 bg-white border rounded-md">
              <FileText className="h-8 w-8 text-blue-500 flex-shrink-0" />
              <div className="flex-grow">
                <p className="font-medium text-gray-800">{file.name}</p>
                <p className="text-sm text-gray-500">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
              </div>
              <button onClick={() => setFile(null)} className="p-1 text-gray-400 hover:text-red-600 rounded-full">
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}

        {isUploading && (
          <div className="w-full">
            <p className="text-sm font-medium text-gray-600 mb-2">{t('upload.uploading')} {file.name}...</p>
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div
                className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
            <p className="text-xs text-gray-500 mt-1">{uploadProgress}%</p>
          </div>
        )}
      </div>

      <div className="mt-6 text-right">
        <button
          onClick={handleUpload}
          disabled={!file || isUploading || isViewOnly}
          className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          {isUploading ? (
            <>
              <Loader2 className="animate-spin h-5 w-5 mr-2" />
              {t('upload.uploading')}...
            </>
          ) : (
            <>
              <UploadCloud className="h-5 w-5 mr-2" />
              {t('upload.uploadProcess')}
            </>
          )}
        </button>
      </div>
    </div>
  );
}

// --- Document Status Component ---
function DocumentStatus({ documents, fetchDocuments }) {
  const { t } = useTranslation();

  // Auto-refresh for processing documents
  useEffect(() => {
    const processingIds = documents.filter(d => d.status === 'processing').map(d => d.id);

    if (processingIds.length > 0) {
      const interval = setInterval(() => {
        fetchDocuments();
      }, 3000); // Poll every 3 seconds
      return () => clearInterval(interval);
    }
  }, [documents, fetchDocuments]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'processing':
        return <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />;
      case 'completed':
        return <Check className="h-4 w-4 text-green-500" />;
      case 'failed':
        return <X className="h-4 w-4 text-red-500" />;
      default:
        return <FileClock className="h-4 w-4 text-gray-400" />;
    }
  };

  const getProgressBar = (doc) => {
    if (doc.status !== 'processing' || doc.progress_percentage === null || doc.progress_percentage === undefined) {
      return null;
    }
    const percentage = doc.progress_percentage || 0;
    return (
      <div className="mt-2">
        <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
          <span>{doc.processed_pages || 0} / {doc.total_pages || 0} {t('documentStatus.pages', 'pages')}</span>
          <span>{percentage.toFixed(1)}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${Math.min(percentage, 100)}%` }}
          ></div>
        </div>
      </div>
    );
  };

  return (
    <div className="p-6 md:p-10 border-t border-gray-200 bg-gray-50">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-700">{t('documentStatus.title')}</h3>
        <button
          onClick={fetchDocuments}
          className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center space-x-1"
        >
          <Loader2 className="h-4 w-4" />
          <span>{t('common.refresh')}</span>
        </button>
      </div>
      <div className="max-h-60 overflow-y-auto space-y-2 pr-2">
        {documents.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-4">{t('documentStatus.noDocuments')}</p>
        ) : (
          documents.map((doc) => (
            <div key={doc.id} className="p-3 bg-white border rounded-md shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3 flex-1 min-w-0">
                  {getStatusIcon(doc.status)}
                  <span className="text-sm font-medium text-gray-800 truncate">{doc.filename}</span>
                </div>
                <span className="text-xs font-mono capitalize px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 ml-2">{t(`documentStatus.status.${doc.status}`, doc.status)}</span>
              </div>
              {getProgressBar(doc)}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// --- Documents Management ---
// Wrapper component for route-based DocumentsView
function DocumentsViewWithRoute({ user, fetchDocuments }) {
  const { documentId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const pageParam = searchParams.get('p');
  const page = pageParam ? parseInt(pageParam, 10) : 1;

  return (
    <>
      <NavBar view="documents" setView={() => { }} />
      <DocumentsView
        documentId={documentId ? parseInt(documentId, 10) : null}
        page={page}
        user={user}
        onPageChange={(newPage) => {
          if (documentId) {
            setSearchParams({ p: newPage.toString() });
          }
        }}
      />
      <DocumentStatus documents={[]} fetchDocuments={fetchDocuments} />
    </>
  );
}

function DocumentsView({ initialDocumentId, documentId, page: initialPage = 1, user, onPageChange }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [resetOnRetry, setResetOnRetry] = useState(false);

  // Use documentId from route if available, otherwise use initialDocumentId
  const targetDocId = documentId || initialDocumentId;

  const fetchDocs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/documents`);
      setDocuments(res.data || []);
    } catch (e) {
      console.error(e);
      showToast(t('toast.failedToLoadDocuments'), 'error');
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  // Select document from URL or initialDocumentId
  useEffect(() => {
    if (targetDocId && documents.length > 0) {
      const found = documents.find(d => d.id === targetDocId);
      if (found) {
        setSelectedDoc(found);
        // Update URL if not already set
        if (!documentId && window.location.pathname !== `/documents/${targetDocId}`) {
          navigate(`/documents/${targetDocId}${initialPage > 1 ? `?p=${initialPage}` : ''}`, { replace: true });
        }
      }
    }
  }, [targetDocId, documents, documentId, initialPage, navigate]);

  // Handle document selection - update URL
  const handleDocumentSelect = (doc) => {
    setSelectedDoc(doc);
    const pageParam = searchParams.get('p');
    navigate(`/documents/${doc.id}${pageParam ? `?p=${pageParam}` : ''}`);
  };

  // Auto-refresh for processing documents
  useEffect(() => {
    const processingIds = documents.filter(d => d.status === 'processing').map(d => d.id);
    if (processingIds.length > 0) {
      const interval = setInterval(() => {
        fetchDocs();
      }, 3000); // Poll every 3 seconds
      return () => clearInterval(interval);
    }
  }, [documents, fetchDocs]);

  return (
    <div className="p-6 md:p-10">
      <h2 className="text-xl font-semibold text-gray-700 mb-4">{t('documents.title')}</h2>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <div className="border rounded-lg overflow-hidden bg-white">
            <div className="p-3 flex items-center justify-between border-b">
              <span className="font-medium">{t('documents.allDocuments')}</span>
              <button onClick={fetchDocs} className="text-sm text-blue-600">{t('common.refresh')}</button>
            </div>
            <div className="max-h-[480px] overflow-y-auto">
              {loading ? (
                <div className="p-6 text-center text-gray-500">{t('common.loading')}</div>
              ) : documents.length === 0 ? (
                <div className="p-6 text-center text-gray-500">{t('documents.noDocuments', 'No documents')}</div>
              ) : (
                documents.map((d) => {
                  const isProcessing = d.status === 'processing';
                  const progressPercentage = d.progress_percentage || 0;
                  return (
                    <button
                      key={d.id}
                      onClick={() => handleDocumentSelect(d)}
                      className={`w-full text-left p-3 border-b hover:bg-gray-50 ${selectedDoc?.id === d.id ? 'bg-blue-50' : ''}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-gray-800 truncate">{d.filename}</span>
                        <span className="text-xs capitalize px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 ml-2">{t(`documentStatus.status.${d.status}`, d.status)}</span>
                      </div>
                      {isProcessing && (progressPercentage !== null && progressPercentage !== undefined) && (
                        <div className="mt-2">
                          <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                            <span>{d.processed_pages || 0} / {d.total_pages || 0} {t('documentStatus.pages', 'pages')}</span>
                            <span>{progressPercentage.toFixed(1)}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-1.5">
                            <div
                              className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
                              style={{ width: `${Math.min(progressPercentage, 100)}%` }}
                            ></div>
                          </div>
                        </div>
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>
        <div className="lg:col-span-2">
          {!selectedDoc ? (
            <div className="p-6 border rounded-lg bg-white text-gray-500">{t('documents.selectDocument')}</div>
          ) : (
            <DocumentDetail
              document={selectedDoc}
              onRetry={() => fetchDocs()}
              resetOnRetry={resetOnRetry}
              setResetOnRetry={setResetOnRetry}
              user={user}
              initialPage={initialPage}
              onPageChange={onPageChange}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// Unified RetryModal Component
function RetryModal({
  isOpen,
  onClose,
  onRetryFull,
  onRetryTranslations,
  resetOnRetry,
  setResetOnRetry,
  canRetryTranslations = true,
  level = "document" // "document" or "page"
}) {
  const { t } = useTranslation();
  const isDocumentLevel = level === "document";

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" onClick={onClose}>
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose}></div>

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="px-6 py-4 border-b flex items-center justify-between bg-gray-50">
            <h3 className="text-lg font-semibold text-gray-900">
              {t('documents.retryOptions', 'Retry Options')}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-4">
            <div className="flex items-center justify-end mb-4">
              <label className="flex items-center space-x-2 text-sm text-gray-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={resetOnRetry}
                  onChange={(e) => setResetOnRetry(e.target.checked)}
                  className="cursor-pointer"
                />
                <span>{t('documents.resetExistingData')}</span>
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Full Retry Option */}
              <button
                onClick={() => {
                  onRetryFull();
                  onClose();
                }}
                className="group relative p-4 border-2 border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 hover:shadow-md transition-all text-left bg-white"
              >
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg group-hover:bg-blue-200 transition-colors flex-shrink-0">
                    <FileSearch className="h-5 w-5 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-gray-800">
                        {isDocumentLevel ? t('documents.retryProcessing') : t('documents.retryPage')}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 leading-relaxed">
                      {isDocumentLevel
                        ? t('documents.retryProcessingDesc', 'Re-runs OCR and all translation steps. Use when OCR needs improvement.')
                        : t('documents.retryPageDesc', 'Re-runs term extraction, translation, and embedding for this page.')
                      }
                    </p>
                  </div>
                </div>
              </button>

              {/* Translation-Only Retry Option */}
              {canRetryTranslations ? (
                <button
                  onClick={() => {
                    onRetryTranslations();
                    onClose();
                  }}
                  className="group relative p-4 border-2 border-gray-200 rounded-lg hover:border-green-400 hover:bg-green-50 hover:shadow-md transition-all text-left bg-white"
                >
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-green-100 rounded-lg group-hover:bg-green-200 transition-colors flex-shrink-0">
                      <Languages className="h-5 w-5 text-green-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="font-semibold text-gray-800">
                          {isDocumentLevel
                            ? t('documents.retryTranslations', 'Retry Translations Only')
                            : t('documents.retryPageTranslations', 'Retry Translations Only')
                          }
                        </span>
                        <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full flex items-center gap-1">
                          <DollarSign className="h-3 w-3" />
                          {t('documents.costSaving', 'Saves Costs')}
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 leading-relaxed">
                        {isDocumentLevel
                          ? t('documents.retryTranslationsDesc', 'Keeps existing OCR results, only refreshes translations. Ideal when OCR is good but translations need improvement.')
                          : t('documents.retryPageTranslationsDesc', 'Keeps existing OCR text, only refreshes translations for this page. Ideal when OCR is good but translations need improvement.')
                        }
                      </p>
                    </div>
                  </div>
                </button>
              ) : (
                <div className="p-4 border-2 border-gray-200 rounded-lg bg-gray-50 opacity-60 cursor-not-allowed">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-gray-200 rounded-lg flex-shrink-0">
                      <Languages className="h-5 w-5 text-gray-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-gray-500">
                          {isDocumentLevel
                            ? t('documents.retryTranslations', 'Retry Translations Only')
                            : t('documents.retryPageTranslations', 'Retry Translations Only')
                          }
                        </span>
                      </div>
                      <p className="text-xs text-gray-500">
                        {t('documents.retryTranslationsUnavailable', 'Available after OCR completes.')}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t bg-gray-50 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              {t('common.cancel', 'Cancel')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Statistics Modal Component
function StatisticsModal({ isOpen, onClose, statistics, loading }) {
  if (!isOpen) return null;

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen px-4">
          <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose}></div>
          <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-8 text-center">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-blue-600" />
              <p className="mt-4 text-gray-600">Loading statistics...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Handle empty or no translation cases
  if (!statistics || (statistics.total_sentence_pairs === 0 && (!statistics.total_sentences || statistics.total_sentences === 0))) {
    return (
      <div className="fixed inset-0 z-50 overflow-y-auto" onClick={onClose}>
        <div className="flex items-center justify-center min-h-screen px-4">
          <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose}></div>
          <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b flex items-center justify-between bg-gray-50">
              <h3 className="text-lg font-semibold text-gray-900">Dataset Statistics</h3>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-500 transition-colors">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="px-6 py-8">
              <div className="text-center mb-4">
                <p className="text-gray-600 mb-2">
                  {statistics?.message || "No training data available. Process some documents first."}
                </p>
                {statistics?.total_sentences > 0 && (
                  <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm text-yellow-800">
                      <strong>Note:</strong> Found {statistics.total_sentences} sentences, but none have translations yet.
                      <br />
                      Sentences are translated during term processing when context sentences are provided by the term extraction model.
                    </p>
                  </div>
                )}
              </div>
            </div>
            <div className="px-6 py-4 border-t bg-gray-50 flex justify-end">
              <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors">
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Handle case where sentences exist but no translations yet
  if (statistics.total_sentence_pairs === 0 && statistics.total_sentences > 0) {
    return (
      <div className="fixed inset-0 z-50 overflow-y-auto" onClick={onClose}>
        <div className="flex items-center justify-center min-h-screen px-4">
          <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose}></div>
          <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b flex items-center justify-between bg-gray-50">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-blue-600" />
                Dataset Statistics
              </h3>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-500 transition-colors">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="px-6 py-4 max-h-[calc(100vh-200px)] overflow-y-auto">
              <div className="mb-6">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-yellow-800">
                    <strong>⚠️ No translations yet:</strong> Found {statistics.total_sentences} sentences, but none have translations.
                    <br />
                    Sentences are translated during term processing when the term extraction model provides context sentences.
                  </p>
                </div>
                <h4 className="text-md font-semibold text-gray-900 mb-3">Overview</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="text-sm font-medium text-gray-600 mb-1">Total Sentences</div>
                    <div className="text-2xl font-bold text-gray-900">{statistics.total_sentences || 0}</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="text-sm font-medium text-gray-600 mb-1">With Translations</div>
                    <div className="text-2xl font-bold text-gray-900">{statistics.sentences_with_translations || 0}</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="text-sm font-medium text-gray-600 mb-1">Translation Coverage</div>
                    <div className="text-2xl font-bold text-gray-900">{statistics.translation_coverage || 0}%</div>
                  </div>
                </div>
              </div>
              {statistics.message && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">{statistics.message}</p>
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t bg-gray-50 flex justify-end">
              <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors">
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const formatNumber = (num) => {
    if (num === null || num === undefined) return 'N/A';
    if (typeof num === 'number') {
      return num.toLocaleString('en-US', { maximumFractionDigits: 2 });
    }
    return num;
  };

  const StatCard = ({ title, value, subtitle }) => (
    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
      <div className="text-sm font-medium text-gray-600 mb-1">{title}</div>
      <div className="text-2xl font-bold text-gray-900">{value}</div>
      {subtitle && <div className="text-xs text-gray-500 mt-1">{subtitle}</div>}
    </div>
  );

  const StatRow = ({ label, value }) => (
    <div className="flex justify-between py-2 border-b border-gray-100">
      <span className="text-sm text-gray-600">{label}</span>
      <span className="text-sm font-medium text-gray-900">{value}</span>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" onClick={onClose}>
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose}></div>
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-5xl sm:w-full" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="px-6 py-4 border-b flex items-center justify-between bg-gray-50">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-blue-600" />
              Dataset Statistics
            </h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-500 transition-colors">
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-4 max-h-[calc(100vh-200px)] overflow-y-auto">
            {/* Overview */}
            <div className="mb-6">
              <h4 className="text-md font-semibold text-gray-900 mb-3">Overview</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard title="Total Pairs" value={formatNumber(statistics.overview?.total_sentence_pairs)} />
                <StatCard title="Documents" value={`${formatNumber(statistics.overview?.documents_with_translations)}/${formatNumber(statistics.overview?.total_documents)}`} subtitle={`${formatNumber(statistics.overview?.coverage_percentage)}% coverage`} />
                <StatCard title="Pages" value={`${formatNumber(statistics.overview?.pages_with_translations)}/${formatNumber(statistics.overview?.total_pages)}`} subtitle={`${formatNumber(statistics.overview?.pages_coverage_percentage)}% coverage`} />
              </div>
            </div>

            {/* Language Distribution */}
            {statistics.language_distribution && (
              <div className="mb-6">
                <h4 className="text-md font-semibold text-gray-900 mb-3">Language Distribution</h4>
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  {Object.entries(statistics.language_distribution).map(([lang, data]) => (
                    <div key={lang} className="mb-2 last:mb-0">
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm font-medium text-gray-700">{lang}</span>
                        <span className="text-sm text-gray-600">{formatNumber(data.count)} ({formatNumber(data.percentage)}%)</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-blue-600 h-2 rounded-full" style={{ width: `${data.percentage}%` }}></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Source Text Statistics */}
            {statistics.source_text_statistics && (
              <div className="mb-6">
                <h4 className="text-md font-semibold text-gray-900 mb-3">Source Text Statistics</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <h5 className="text-sm font-medium text-gray-700 mb-2">Characters</h5>
                    {statistics.source_text_statistics.characters && (
                      <div className="space-y-1">
                        <StatRow label="Min" value={formatNumber(statistics.source_text_statistics.characters.min)} />
                        <StatRow label="Max" value={formatNumber(statistics.source_text_statistics.characters.max)} />
                        <StatRow label="Mean" value={formatNumber(statistics.source_text_statistics.characters.mean)} />
                        <StatRow label="Median" value={formatNumber(statistics.source_text_statistics.characters.median)} />
                        <StatRow label="P25" value={formatNumber(statistics.source_text_statistics.characters.p25)} />
                        <StatRow label="P75" value={formatNumber(statistics.source_text_statistics.characters.p75)} />
                        <StatRow label="P95" value={formatNumber(statistics.source_text_statistics.characters.p95)} />
                      </div>
                    )}
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <h5 className="text-sm font-medium text-gray-700 mb-2">Words</h5>
                    {statistics.source_text_statistics.words && (
                      <div className="space-y-1">
                        <StatRow label="Min" value={formatNumber(statistics.source_text_statistics.words.min)} />
                        <StatRow label="Max" value={formatNumber(statistics.source_text_statistics.words.max)} />
                        <StatRow label="Mean" value={formatNumber(statistics.source_text_statistics.words.mean)} />
                        <StatRow label="Median" value={formatNumber(statistics.source_text_statistics.words.median)} />
                        <StatRow label="P25" value={formatNumber(statistics.source_text_statistics.words.p25)} />
                        <StatRow label="P75" value={formatNumber(statistics.source_text_statistics.words.p75)} />
                        <StatRow label="P95" value={formatNumber(statistics.source_text_statistics.words.p95)} />
                      </div>
                    )}
                  </div>
                </div>
                {statistics.source_text_statistics.estimated_tokens && (
                  <div className="mt-3 bg-blue-50 rounded-lg p-3 border border-blue-200">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-blue-900">Estimated Tokens</span>
                      <span className="text-sm text-blue-700">
                        Total: {formatNumber(statistics.source_text_statistics.estimated_tokens.total)} | 
                        Mean: {formatNumber(statistics.source_text_statistics.estimated_tokens.mean)}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Target Text Statistics */}
            {statistics.target_text_statistics && (
              <div className="mb-6">
                <h4 className="text-md font-semibold text-gray-900 mb-3">Target Text Statistics</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <h5 className="text-sm font-medium text-gray-700 mb-2">Characters</h5>
                    {statistics.target_text_statistics.characters && (
                      <div className="space-y-1">
                        <StatRow label="Min" value={formatNumber(statistics.target_text_statistics.characters.min)} />
                        <StatRow label="Max" value={formatNumber(statistics.target_text_statistics.characters.max)} />
                        <StatRow label="Mean" value={formatNumber(statistics.target_text_statistics.characters.mean)} />
                        <StatRow label="Median" value={formatNumber(statistics.target_text_statistics.characters.median)} />
                        <StatRow label="P25" value={formatNumber(statistics.target_text_statistics.characters.p25)} />
                        <StatRow label="P75" value={formatNumber(statistics.target_text_statistics.characters.p75)} />
                        <StatRow label="P95" value={formatNumber(statistics.target_text_statistics.characters.p95)} />
                      </div>
                    )}
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <h5 className="text-sm font-medium text-gray-700 mb-2">Words</h5>
                    {statistics.target_text_statistics.words && (
                      <div className="space-y-1">
                        <StatRow label="Min" value={formatNumber(statistics.target_text_statistics.words.min)} />
                        <StatRow label="Max" value={formatNumber(statistics.target_text_statistics.words.max)} />
                        <StatRow label="Mean" value={formatNumber(statistics.target_text_statistics.words.mean)} />
                        <StatRow label="Median" value={formatNumber(statistics.target_text_statistics.words.median)} />
                        <StatRow label="P25" value={formatNumber(statistics.target_text_statistics.words.p25)} />
                        <StatRow label="P75" value={formatNumber(statistics.target_text_statistics.words.p75)} />
                        <StatRow label="P95" value={formatNumber(statistics.target_text_statistics.words.p95)} />
                      </div>
                    )}
                  </div>
                </div>
                {statistics.target_text_statistics.estimated_tokens && (
                  <div className="mt-3 bg-green-50 rounded-lg p-3 border border-green-200">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-green-900">Estimated Tokens</span>
                      <span className="text-sm text-green-700">
                        Total: {formatNumber(statistics.target_text_statistics.estimated_tokens.total)} | 
                        Mean: {formatNumber(statistics.target_text_statistics.estimated_tokens.mean)}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Translation Ratio */}
            {statistics.translation_ratio && (
              <div className="mb-6">
                <h4 className="text-md font-semibold text-gray-900 mb-3">Translation Ratio (Target/Source)</h4>
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="mb-2">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm font-medium text-gray-700">Mean Ratio</span>
                      <span className="text-sm text-gray-900 font-semibold">{formatNumber(statistics.translation_ratio.mean_ratio)}</span>
                    </div>
                    <div className="text-xs text-gray-500">
                      {statistics.translation_ratio.mean_ratio > 1 ? 'Target is longer' : statistics.translation_ratio.mean_ratio < 1 ? 'Source is longer' : 'Similar length'}
                    </div>
                  </div>
                  {statistics.translation_ratio.characters && (
                    <div className="space-y-1 mt-3">
                      <StatRow label="Min Ratio" value={formatNumber(statistics.translation_ratio.characters.min)} />
                      <StatRow label="Max Ratio" value={formatNumber(statistics.translation_ratio.characters.max)} />
                      <StatRow label="Median Ratio" value={formatNumber(statistics.translation_ratio.characters.median)} />
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Sample Sentences */}
            {statistics.sample_sentences && statistics.sample_sentences.length > 0 && (
              <div className="mb-6">
                <h4 className="text-md font-semibold text-gray-900 mb-3">Sample Sentences</h4>
                <div className="space-y-3">
                  {statistics.sample_sentences.slice(0, 5).map((sample, idx) => (
                    <div key={idx} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <div className="mb-2">
                        <div className="text-xs font-medium text-gray-500 mb-1">Source ({sample.source_language})</div>
                        <div className="text-sm text-gray-800">{sample.source}</div>
                        <div className="text-xs text-gray-400 mt-1">{sample.source_length} chars</div>
                      </div>
                      <div>
                        <div className="text-xs font-medium text-gray-500 mb-1">Target</div>
                        <div className="text-sm text-gray-800">{sample.target}</div>
                        <div className="text-xs text-gray-400 mt-1">{sample.target_length} chars</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Export Info */}
            {statistics.export_info && (
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <div className="text-sm">
                  <div className="font-medium text-blue-900 mb-2">Export Information</div>
                  <div className="space-y-1 text-blue-700">
                    <div>Target Language: <span className="font-medium">{statistics.export_info.target_language}</span></div>
                    <div>Format: <span className="font-medium">{statistics.export_info.format}</span></div>
                    <div>Endpoint: <span className="font-medium font-mono text-xs">{statistics.export_info.export_endpoint}</span></div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t bg-gray-50 flex justify-end">
            <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function DocumentDetail({ document, onRetry, resetOnRetry, setResetOnRetry, user, initialPage = 1, onPageChange }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [pages, setPages] = useState([]);
  const [page, setPage] = useState(initialPage);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [openPageId, setOpenPageId] = useState(null);
  const [termsByPage, setTermsByPage] = useState({});
  const [modelConfig, setModelConfig] = useState({ expert_model: "", expert_2_model: "", custom_model: "" });
  const [selectedPage, setSelectedPage] = useState(null);
  const [showRetryModal, setShowRetryModal] = useState(false);
  const [retryModalPageId, setRetryModalPageId] = useState(null);

  // Helper function to check if a language group should be hidden based on source_language
  const shouldHideLanguageGroup = (sourceLang, languageGroup) => {
    if (!sourceLang) return false;
    const lang = sourceLang.toLowerCase();
    if (languageGroup === 'en' && (lang === 'en' || lang.startsWith('en'))) return true;
    if (languageGroup === 'cn' && (lang === 'zh-cn' || lang === 'zh-tw' || lang === 'zh' || lang.startsWith('zh'))) return true;
    if (languageGroup === 'vi' && (lang === 'vi' || lang.startsWith('vi'))) return true;
    return false;
  };

  const openPageModal = async (pageObj) => {
    setSelectedPage(pageObj);
    // Load terms if not already loaded
    if (!termsByPage[pageObj.id]) {
      try {
        const res = await axios.get(`${API_URL}/pages/${pageObj.id}/terms`, { params: { page: 1, page_size: 50 } });
        setTermsByPage((prev) => ({ ...prev, [pageObj.id]: res.data.terms || [] }));
      } catch (e) {
        console.error(e);
        showToast(t('toast.failedToLoadTerms'), 'error');
      }
    }
  };

  // Fetch model configuration
  useEffect(() => {
    const fetchModelConfig = async () => {
      try {
        const response = await axios.get(`${API_URL}/config/models`);
        setModelConfig(response.data);
      } catch (error) {
        console.error("Error fetching model config:", error);
      }
    };
    fetchModelConfig();
  }, []);

  const fetchPages = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/pages`, { params: { document_id: document.id, page, page_size: 10 } });
      setPages(res.data.pages || []);
      setTotalPages(res.data.total_pages || 1);
    } catch (e) {
      console.error(e);
      showToast(t('toast.failedToLoadPages'), 'error');
    } finally {
      setLoading(false);
    }
  }, [document.id, page, t]);

  useEffect(() => { fetchPages(); }, [fetchPages]);

  // Sync page state with URL when initialPage changes
  useEffect(() => {
    if (initialPage !== page) {
      setPage(initialPage);
    }
  }, [initialPage, page]);

  // Handle page change - update URL and call callback
  const handlePageChange = (newPage) => {
    setPage(newPage);
    if (onPageChange) {
      onPageChange(newPage);
    } else {
      // Fallback: update URL directly if no callback provided
      const currentPath = window.location.pathname;
      const match = currentPath.match(/\/documents\/(\d+)/);
      if (match) {
        const url = newPage > 1 ? `/documents/${match[1]}?p=${newPage}` : `/documents/${match[1]}`;
        navigate(url, { replace: true });
      }
    }
  };

  const toggleTerms = async (pageObj) => {
    if (openPageId === pageObj.id) {
      setOpenPageId(null);
      return;
    }
    setOpenPageId(pageObj.id);
    if (!termsByPage[pageObj.id]) {
      try {
        const res = await axios.get(`${API_URL}/pages/${pageObj.id}/terms`, { params: { page: 1, page_size: 50 } });
        setTermsByPage((prev) => ({ ...prev, [pageObj.id]: res.data.terms || [] }));
      } catch (e) {
        console.error(e);
        showToast(t('toast.failedToLoadTerms'), 'error');
      }
    }
  };

  const retryPage = async (pageId, reset = true) => {
    try {
      await axios.post(`${API_URL}/pages/${pageId}/retry`, null, { params: { reset } });
      showToast(t('toast.pageReprocessingStarted'), 'success');
      // reload terms for this page when done; for now, refetch immediate snapshot
      const res = await axios.get(`${API_URL}/pages/${pageId}/terms`, { params: { page: 1, page_size: 50 } });
      setTermsByPage((prev) => ({ ...prev, [pageId]: res.data.terms || [] }));
      onRetry && onRetry(); // Refresh document list
    } catch (e) {
      console.error(e);
      showToast(e.response?.data?.detail || t('toast.failedToRetryPage'), 'error');
    }
  };

  const retryPageTranslations = async (pageId, reset = true) => {
    try {
      await axios.post(`${API_URL}/pages/${pageId}/retry-translations`, null, { params: { reset } });
      showToast(t('toast.pageTranslationRetryStarted', 'Page translation retry started (OCR skipped)'), 'success');
      // reload terms for this page when done; for now, refetch immediate snapshot
      const res = await axios.get(`${API_URL}/pages/${pageId}/terms`, { params: { page: 1, page_size: 50 } });
      setTermsByPage((prev) => ({ ...prev, [pageId]: res.data.terms || [] }));
      onRetry && onRetry(); // Refresh document list
    } catch (e) {
      console.error(e);
      showToast(e.response?.data?.detail || t('toast.failedToRetryPageTranslations', 'Failed to retry page translations'), 'error');
    }
  };

  const retryTerm = async (termId, pageId) => {
    try {
      await axios.post(`${API_URL}/terms/${termId}/retry`, null, { params: { retranslate: true, reembed: true } });
      showToast(t('toast.termRefreshed'), 'success');
      // refresh terms list for the page
      const res = await axios.get(`${API_URL}/pages/${pageId}/terms`, { params: { page: 1, page_size: 50 } });
      setTermsByPage((prev) => ({ ...prev, [pageId]: res.data.terms || [] }));
    } catch (e) {
      console.error(e);
      showToast(e.response?.data?.detail || t('toast.failedToRetryTerm'), 'error');
    }
  };

  const deleteTerm = async (termId, pageId, originalText) => {
    if (!window.confirm(t('review.confirmDelete', `Are you sure you want to delete "${originalText}"?`))) {
      return;
    }
    try {
      await axios.delete(`${API_URL}/terms/${termId}`);
      showToast(t('review.termDeleted', 'Term deleted successfully'), 'success');
      // refresh terms list for the page
      const res = await axios.get(`${API_URL}/pages/${pageId}/terms`, { params: { page: 1, page_size: 50 } });
      setTermsByPage((prev) => ({ ...prev, [pageId]: res.data.terms || [] }));
    } catch (e) {
      console.error(e);
      showToast(e.response?.data?.detail || t('review.deleteFailed', 'Failed to delete term'), 'error');
    }
  };

  const retry = async () => {
    try {
      await axios.post(`${API_URL}/documents/${document.id}/retry`, null, { params: { reset: resetOnRetry } });
      showToast(t('toast.reprocessingStarted'), 'success');
      onRetry && onRetry();
    } catch (e) {
      console.error(e);
      showToast(e.response?.data?.detail || t('toast.failedToDispatchRetry'), 'error');
    }
  };

  const retryTranslations = async () => {
    try {
      await axios.post(`${API_URL}/documents/${document.id}/retry-translations`, null, { params: { reset: resetOnRetry } });
      showToast(t('toast.translationRetryStarted', 'Translation retry started (OCR skipped)'), 'success');
      onRetry && onRetry();
    } catch (e) {
      console.error(e);
      showToast(e.response?.data?.detail || t('toast.failedToRetryTranslations', 'Failed to retry translations'), 'error');
    }
  };

  return (
    <div className="border rounded-lg bg-white">

      {/* Document Header with Retry Button */}
      <div className="p-4 border-b">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="text-lg font-semibold text-gray-800">{document.filename}</div>
            <div className="text-sm text-gray-500 mt-1">{t('documents.status')} <span className="capitalize">{t(`documentStatus.status.${document.status}`, document.status)}</span></div>
            {document.status === 'processing' && document.progress_percentage !== null && document.progress_percentage !== undefined && (
              <div className="mt-2">
                <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                  <span>{document.processed_pages || 0} / {document.total_pages || 0} {t('documentStatus.pages', 'pages')}</span>
                  <span>{document.progress_percentage.toFixed(1)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${Math.min(document.progress_percentage, 100)}%` }}
                  ></div>
                </div>
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            {user?.role !== 'viewer' && (
              <button
                onClick={() => setShowRetryModal(true)}
                className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors flex items-center gap-1.5"
              >
                <RefreshCw className="h-4 w-4" />
                {t('documents.retry', 'Retry')}
              </button>
            )}
            {user?.role === 'viewer' && (
              <span className="text-sm text-gray-500">{t('review.viewOnly', 'View-only mode')}</span>
            )}
          </div>
        </div>
      </div>

      {/* Document Retry Modal */}
      {user?.role !== 'viewer' && !retryModalPageId && (
        <RetryModal
          isOpen={showRetryModal && !retryModalPageId}
          onClose={() => setShowRetryModal(false)}
          onRetryFull={() => retry()}
          onRetryTranslations={() => retryTranslations()}
          resetOnRetry={resetOnRetry}
          setResetOnRetry={setResetOnRetry}
          canRetryTranslations={pages.length > 0}
          level="document"
        />
      )}

      {/* Page Retry Modal */}
      {user?.role !== 'viewer' && retryModalPageId && (
        <RetryModal
          isOpen={showRetryModal && retryModalPageId !== null}
          onClose={() => {
            setShowRetryModal(false);
            setRetryModalPageId(null);
          }}
          onRetryFull={() => {
            const pageId = retryModalPageId;
            setRetryModalPageId(null);
            setShowRetryModal(false);
            retryPage(pageId, resetOnRetry);
          }}
          onRetryTranslations={() => {
            const pageId = retryModalPageId;
            setRetryModalPageId(null);
            setShowRetryModal(false);
            retryPageTranslations(pageId, resetOnRetry);
          }}
          resetOnRetry={resetOnRetry}
          setResetOnRetry={setResetOnRetry}
          canRetryTranslations={retryModalPageId && pages.find(p => p.id === retryModalPageId)?.text && pages.find(p => p.id === retryModalPageId).text.trim().length > 10}
          level="page"
        />
      )}

      <div className="p-4">
        {loading ? (
          <div className="text-gray-500">{t('documents.loadingPages')}</div>
        ) : pages.length === 0 ? (
          <div className="text-gray-500">{t('documents.noPages')}</div>
        ) : (
          <div className="space-y-2">
            {pages.map((p) => (
              <div key={p.id} className="border rounded-md">
                <div className="w-full p-3 bg-gray-50 flex items-start justify-between">
                  <button onClick={() => toggleTerms(p)} className="text-left">
                    <div className="text-sm text-gray-700">{t('documents.page')} {p.page_number}</div>
                    <div className="text-gray-800 line-clamp-2">{p.text}</div>
                  </button>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={(e) => { e.stopPropagation(); openPageModal(p); }}
                      className="px-3 py-1.5 text-xs bg-gray-600 text-white rounded flex items-center gap-1 hover:bg-gray-700 transition-colors h-8 whitespace-nowrap"
                      title={t('documents.viewDetails')}
                    >
                      <Eye className="h-3 w-3 flex-shrink-0" />
                      <span>{t('documents.viewDetails')}</span>
                    </button>
                    {user?.role !== 'viewer' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setRetryModalPageId(p.id);
                          setShowRetryModal(true);
                        }}
                        className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors flex items-center gap-1 h-8 whitespace-nowrap"
                        title={t('documents.retry', 'Retry')}
                      >
                        <RefreshCw className="h-3 w-3 flex-shrink-0" />
                        <span>{t('documents.retry', 'Retry')}</span>
                      </button>
                    )}
                    <button
                      onClick={() => toggleTerms(p)}
                      className="px-3 py-1.5 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors border border-gray-300 h-8 whitespace-nowrap"
                    >
                      {openPageId === p.id ? t('review.hideTerms') : t('review.viewTerms')}
                    </button>
                  </div>
                </div>
                {openPageId === p.id && (
                  <div className="p-3">

                    {(termsByPage[p.id] || []).length === 0 ? (
                      <div className="text-sm text-gray-500">{t('documents.noTermsExtracted')}</div>
                    ) : (() => {
                      // Use majority vote: find the most common source language
                      const terms = termsByPage[p.id] || [];
                      const sourceLangs = terms.map(t => t.source_language).filter(Boolean);
                      let commonSourceLang = null;
                      if (sourceLangs.length > 0) {
                        // Count occurrences of each language
                        const langCounts = {};
                        sourceLangs.forEach(lang => {
                          langCounts[lang] = (langCounts[lang] || 0) + 1;
                        });
                        // Find the most common language
                        const mostCommonLang = Object.keys(langCounts).reduce((a, b) =>
                          langCounts[a] > langCounts[b] ? a : b
                        );
                        // Use it if it represents majority (>50%)
                        const majorityThreshold = sourceLangs.length * 0.5;
                        commonSourceLang = langCounts[mostCommonLang] > majorityThreshold ? mostCommonLang : null;
                      }
                      const hideEnGlobal = commonSourceLang && shouldHideLanguageGroup(commonSourceLang, 'en');
                      const hideCnGlobal = commonSourceLang && shouldHideLanguageGroup(commonSourceLang, 'cn');
                      const hideViGlobal = commonSourceLang && shouldHideLanguageGroup(commonSourceLang, 'vi');

                      return (
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('documents.original')}</th>
                              {!hideEnGlobal && (
                                <>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                    <div className="flex items-center gap-1 group relative">
                                      {t('review.expertEn')}
                                      <Info className="h-3 w-3 text-gray-400 cursor-help" />
                                      <span className="absolute left-0 top-full mt-1 w-64 p-2 bg-gray-900 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none">
                                        {t('review.model')} {modelConfig.expert_model || t('common.loading')}
                                      </span>
                                    </div>
                                  </th>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                    <div className="flex items-center gap-1 group relative">
                                      {t('review.expert2En')}
                                      <Info className="h-3 w-3 text-gray-400 cursor-help" />
                                      <span className="absolute left-0 top-full mt-1 w-64 p-2 bg-gray-900 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none">
                                        {t('review.model')} {modelConfig.expert_2_model || t('common.loading')}
                                      </span>
                                    </div>
                                  </th>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                    <div className="flex items-center gap-1 group relative">
                                      {t('review.customEn')}
                                      <Info className="h-3 w-3 text-gray-400 cursor-help" />
                                      <span className="absolute left-0 top-full mt-1 w-64 p-2 bg-gray-900 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none">
                                        {t('review.model')} {modelConfig.custom_model || t('common.loading')}
                                      </span>
                                    </div>
                                  </th>
                                </>
                              )}
                              {!hideCnGlobal && (
                                <>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                    <div className="flex items-center gap-1 group relative">
                                      {t('review.expertCn')}
                                      <Info className="h-3 w-3 text-gray-400 cursor-help" />
                                      <span className="absolute left-0 top-full mt-1 w-64 p-2 bg-gray-900 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none">
                                        {t('review.model')} {modelConfig.expert_model || t('common.loading')}
                                      </span>
                                    </div>
                                  </th>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                    <div className="flex items-center gap-1 group relative">
                                      {t('review.expert2Cn')}
                                      <Info className="h-3 w-3 text-gray-400 cursor-help" />
                                      <span className="absolute left-0 top-full mt-1 w-64 p-2 bg-gray-900 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none">
                                        {t('review.model')} {modelConfig.expert_2_model || t('common.loading')}
                                      </span>
                                    </div>
                                  </th>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                    <div className="flex items-center gap-1 group relative">
                                      {t('review.customCn')}
                                      <Info className="h-3 w-3 text-gray-400 cursor-help" />
                                      <span className="absolute left-0 top-full mt-1 w-64 p-2 bg-gray-900 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none">
                                        {t('review.model')} {modelConfig.custom_model || t('common.loading')}
                                      </span>
                                    </div>
                                  </th>
                                </>
                              )}
                              {!hideViGlobal && (
                                <>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                    <div className="flex items-center gap-1 group relative">
                                      {t('review.expertVi')}
                                      <Info className="h-3 w-3 text-gray-400 cursor-help" />
                                      <span className="absolute left-0 top-full mt-1 w-64 p-2 bg-gray-900 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none">
                                        {t('review.model')} {modelConfig.expert_model || t('common.loading')}
                                      </span>
                                    </div>
                                  </th>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                    <div className="flex items-center gap-1 group relative">
                                      {t('review.expert2Vi')}
                                      <Info className="h-3 w-3 text-gray-400 cursor-help" />
                                      <span className="absolute left-0 top-full mt-1 w-64 p-2 bg-gray-900 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none">
                                        {t('review.model')} {modelConfig.expert_2_model || t('common.loading')}
                                      </span>
                                    </div>
                                  </th>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                    <div className="flex items-center gap-1 group relative">
                                      {t('review.customVi')}
                                      <Info className="h-3 w-3 text-gray-400 cursor-help" />
                                      <span className="absolute left-0 top-full mt-1 w-64 p-2 bg-gray-900 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none">
                                        {t('review.model')} {modelConfig.custom_model || t('common.loading')}
                                      </span>
                                    </div>
                                  </th>
                                </>
                              )}
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('review.status')}</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('review.actions', 'Actions')}</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {terms.map((term) => (
                              <tr key={term.id}>
                                <td className="px-3 py-2 text-sm text-gray-800">{term.original_text}</td>
                                {!hideEnGlobal && (
                                  <>
                                    <td className="px-3 py-2 text-sm">{term.expert_translated_en ?? ""}</td>
                                    <td className="px-3 py-2 text-sm">{term.expert_2_translated_en ?? ""}</td>
                                    <td className="px-3 py-2 text-sm">{term.custom_translated_en}</td>
                                  </>
                                )}
                                {!hideCnGlobal && (
                                  <>
                                    <td className="px-3 py-2 text-sm">{term.expert_translated_cn ?? ""}</td>
                                    <td className="px-3 py-2 text-sm">{term.expert_2_translated_cn ?? ""}</td>
                                    <td className="px-3 py-2 text-sm">{term.custom_translated_cn}</td>
                                  </>
                                )}
                                {!hideViGlobal && (
                                  <>
                                    <td className="px-3 py-2 text-sm">{term.expert_translated_vi ?? ""}</td>
                                    <td className="px-3 py-2 text-sm">{term.expert_2_translated_vi ?? ""}</td>
                                    <td className="px-3 py-2 text-sm">{term.custom_translated_vi ?? ""}</td>
                                  </>
                                )}
                                <td className="px-3 py-2 text-sm">
                                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${term.status === 'reviewed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>{t(`documentStatus.status.${term.status}`, term.status)}</span>
                                </td>
                                <td className="px-3 py-2 text-right">
                                  {user?.role !== 'viewer' ? (
                                    <div className="flex space-x-2 justify-end">
                                      <button onClick={() => retryTerm(term.id, p.id)} className="px-2 py-1 text-xs border rounded hover:bg-gray-50">{t('documents.retryTerm', 'Retry term')}</button>
                                      <button
                                        onClick={() => deleteTerm(term.id, p.id, term.original_text)}
                                        className="px-2 py-1 text-xs border border-red-300 text-red-600 rounded hover:bg-red-50"
                                        title={t('review.delete', 'Delete')}
                                      >
                                        <Trash2 className="h-3 w-3 inline mr-1" />
                                        {t('review.delete', 'Delete')}
                                      </button>
                                    </div>
                                  ) : (
                                    <span className="text-xs text-gray-400">{t('review.viewOnly', 'View-only')}</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      );
                    })()}
                  </div>
                )}
              </div>
            ))}

            <div className="mt-4">
              <Pagination page={page} totalPages={totalPages} onPageChange={handlePageChange} />
            </div>
          </div>
        )}
      </div>

      {/* Page Details Modal */}
      {selectedPage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setSelectedPage(null)}>
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-xl font-semibold text-gray-800">{t('documents.pageDetails')}</h2>
              <button
                onClick={() => setSelectedPage(null)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">{t('documents.pageId')}</label>
                  <div className="mt-1 text-sm text-gray-900 font-mono">{selectedPage.id}</div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">{t('documents.page')}</label>
                  <div className="mt-1 text-sm text-gray-900">{selectedPage.page_number}</div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">{t('documents.fullText')}</label>
                  <div className="mt-2 p-4 bg-gray-50 rounded-md border border-gray-200">
                    <div className="bg-white rounded-md p-3 border border-gray-200 max-h-96 overflow-y-auto prose prose-sm max-w-none">
                      <ReactMarkdown
                        className="text-sm text-gray-700 leading-relaxed"
                        remarkPlugins={[remarkMath]}
                        rehypePlugins={[rehypeKatex, rehypeRaw]}
                        components={{
                          p: ({ node, ...props }) => <p className="mb-2 last:mb-0" {...props} />,
                          h1: ({ node, children, ...props }) => <h1 className="text-lg font-bold mb-2 mt-3 first:mt-0" {...props}>{children}</h1>,
                          h2: ({ node, children, ...props }) => <h2 className="text-base font-bold mb-2 mt-3 first:mt-0" {...props}>{children}</h2>,
                          h3: ({ node, children, ...props }) => <h3 className="text-sm font-bold mb-1 mt-2 first:mt-0" {...props}>{children}</h3>,
                          ul: ({ node, children, ...props }) => <ul className="list-disc list-inside mb-2 space-y-1" {...props}>{children}</ul>,
                          ol: ({ node, children, ...props }) => <ol className="list-decimal list-inside mb-2 space-y-1" {...props}>{children}</ol>,
                          li: ({ node, children, ...props }) => <li className="ml-2" {...props}>{children}</li>,
                          code: ({ node, inline, children, ...props }) =>
                            inline ? (
                              <code className="bg-gray-100 px-1 py-0.5 rounded text-xs font-mono" {...props}>{children}</code>
                            ) : (
                              <code className="block bg-gray-100 p-2 rounded text-xs font-mono overflow-x-auto mb-2" {...props}>{children}</code>
                            ),
                          pre: ({ node, children, ...props }) => <pre className="bg-gray-100 p-2 rounded text-xs font-mono overflow-x-auto mb-2" {...props}>{children}</pre>,
                          blockquote: ({ node, children, ...props }) => <blockquote className="border-l-4 border-gray-300 pl-3 italic my-2" {...props}>{children}</blockquote>,
                          strong: ({ node, children, ...props }) => <strong className="font-semibold" {...props}>{children}</strong>,
                          em: ({ node, children, ...props }) => <em className="italic" {...props}>{children}</em>,
                          table: ({ node, children, ...props }) => (
                            <div className="overflow-x-auto mb-2">
                              <table className="min-w-full border-collapse border border-gray-300 text-xs" {...props}>{children}</table>
                            </div>
                          ),
                          thead: ({ node, children, ...props }) => <thead className="bg-gray-100" {...props}>{children}</thead>,
                          tbody: ({ node, children, ...props }) => <tbody {...props}>{children}</tbody>,
                          tr: ({ node, children, ...props }) => <tr className="border-b border-gray-200" {...props}>{children}</tr>,
                          th: ({ node, children, ...props }) => <th className="border border-gray-300 px-2 py-1 text-left font-semibold" {...props}>{children}</th>,
                          td: ({ node, children, ...props }) => <td className="border border-gray-300 px-2 py-1" {...props}>{children}</td>,
                          hr: ({ node, ...props }) => <hr className="my-3 border-gray-300" {...props} />,
                        }}
                      >
                        {selectedPage.text}
                      </ReactMarkdown>
                    </div>
                  </div>
                </div>
                {(termsByPage[selectedPage.id] || []).length > 0 && (() => {
                  const modalTerms = termsByPage[selectedPage.id] || [];
                  const modalSourceLangs = modalTerms.map(t => t.source_language).filter(Boolean);
                  let modalCommonSourceLang = null;
                  if (modalSourceLangs.length > 0) {
                    // Count occurrences of each language
                    const langCounts = {};
                    modalSourceLangs.forEach(lang => {
                      langCounts[lang] = (langCounts[lang] || 0) + 1;
                    });
                    // Find the most common language
                    const mostCommonLang = Object.keys(langCounts).reduce((a, b) =>
                      langCounts[a] > langCounts[b] ? a : b
                    );
                    // Use it if it represents majority (>50%)
                    const majorityThreshold = modalSourceLangs.length * 0.5;
                    modalCommonSourceLang = langCounts[mostCommonLang] > majorityThreshold ? mostCommonLang : null;
                  }
                  const modalHideEn = modalCommonSourceLang && shouldHideLanguageGroup(modalCommonSourceLang, 'en');

                  return (
                    <div>
                      <label className="text-sm font-medium text-gray-700">{t('review.termsFound')} ({modalTerms.length})</label>
                      <div className="mt-2 max-h-64 overflow-y-auto border border-gray-200 rounded-md">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50 sticky top-0">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('documents.original')}</th>
                              {!modalHideEn && (
                                <>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('review.expertEn')}</th>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('review.expert2En')}</th>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('review.customEn')}</th>
                                </>
                              )}
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('review.status')}</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {modalTerms.map((term) => (
                              <tr key={term.id}>
                                <td className="px-3 py-2 text-sm text-gray-800">{term.original_text}</td>
                                {!modalHideEn && (
                                  <>
                                    <td className="px-3 py-2 text-sm">{term.expert_translated_en ?? ""}</td>
                                    <td className="px-3 py-2 text-sm">{term.expert_2_translated_en ?? ""}</td>
                                    <td className="px-3 py-2 text-sm">{term.custom_translated_en ?? ""}</td>
                                  </>
                                )}
                                <td className="px-3 py-2 text-sm">
                                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${term.status === 'reviewed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                                    {t(`documentStatus.status.${term.status}`, term.status)}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 p-6 border-t bg-gray-50">
              <button
                onClick={() => toggleTerms(selectedPage)}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                {openPageId === selectedPage.id ? t('review.hideTerms') : t('review.viewTerms')}
              </button>
              <button
                onClick={() => setSelectedPage(null)}
                className="px-4 py-2 text-sm bg-gray-600 text-white rounded hover:bg-gray-700"
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// --- Review View Component ---
// Wrapper component for route-based ReviewView
function ReviewViewWithRoute({ user }) {
  const [searchParams, setSearchParams] = useSearchParams();

  // Read URL parameters with defaults
  const pageParam = searchParams.get('p');
  const filterParam = searchParams.get('filter');
  const showDiscrepanciesOnlyParam = searchParams.get('showDiscrepanciesOnly');
  const sortByDiscrepancyParam = searchParams.get('sortByDiscrepancy');
  const documentIdsParam = searchParams.get('documentIds');

  const page = pageParam ? parseInt(pageParam, 10) : 1;
  const filter = filterParam || 'unreviewed';
  const showDiscrepanciesOnly = showDiscrepanciesOnlyParam === 'true';
  const sortByDiscrepancy = sortByDiscrepancyParam === 'true';
  const documentIds = documentIdsParam ? documentIdsParam.split(',').map(id => parseInt(id, 10)).filter(id => !isNaN(id)) : [];

  return (
    <>
      <NavBar view="review" setView={() => { }} />
      <ReviewView
        user={user}
        initialPage={page}
        initialFilter={filter}
        initialShowDiscrepanciesOnly={showDiscrepanciesOnly}
        initialSortByDiscrepancy={sortByDiscrepancy}
        initialDocumentIds={documentIds}
        onPageChange={(newPage) => {
          const params = new URLSearchParams(searchParams);
          if (newPage > 1) {
            params.set('p', newPage.toString());
          } else {
            params.delete('p');
          }
          setSearchParams(params);
        }}
        onFilterChange={(newFilter) => {
          const params = new URLSearchParams(searchParams);
          if (newFilter !== 'unreviewed') {
            params.set('filter', newFilter);
          } else {
            params.delete('filter');
          }
          params.delete('p'); // Reset to page 1
          setSearchParams(params);
        }}
        onShowDiscrepanciesOnlyChange={(value) => {
          const params = new URLSearchParams(searchParams);
          if (value) {
            params.set('showDiscrepanciesOnly', 'true');
          } else {
            params.delete('showDiscrepanciesOnly');
          }
          params.delete('p'); // Reset to page 1
          setSearchParams(params);
        }}
        onSortByDiscrepancyChange={(value) => {
          const params = new URLSearchParams(searchParams);
          if (value) {
            params.set('sortByDiscrepancy', 'true');
          } else {
            params.delete('sortByDiscrepancy');
          }
          params.delete('p'); // Reset to page 1
          setSearchParams(params);
        }}
        onDocumentIdsChange={(newDocumentIds) => {
          const params = new URLSearchParams(searchParams);
          if (newDocumentIds.length > 0) {
            params.set('documentIds', newDocumentIds.join(','));
          } else {
            params.delete('documentIds');
          }
          params.delete('p'); // Reset to page 1
          setSearchParams(params);
        }}
      />
    </>
  );
}

function ReviewView({ user, initialPage = 1, initialFilter = 'unreviewed', initialShowDiscrepanciesOnly = false, initialSortByDiscrepancy = false, initialDocumentIds = [], onPageChange, onFilterChange, onShowDiscrepanciesOnlyChange, onSortByDiscrepancyChange, onDocumentIdsChange }) {
  const { t } = useTranslation();
  const [terms, setTerms] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(initialPage);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [filter, setFilter] = useState(initialFilter); // 'all', 'unreviewed', 'reviewed'
  const [editingId, setEditingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [modelConfig, setModelConfig] = useState({ expert_model: "", expert_2_model: "", custom_model: "" });
  const [showDiscrepanciesOnly, setShowDiscrepanciesOnly] = useState(initialShowDiscrepanciesOnly);
  const [sortByDiscrepancy, setSortByDiscrepancy] = useState(initialSortByDiscrepancy);
  const [retryTaskStatus, setRetryTaskStatus] = useState(null);
  const [isRetrying, setIsRetrying] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [selectedDocumentIds, setSelectedDocumentIds] = useState(initialDocumentIds);
  const [isDocumentDropdownOpen, setIsDocumentDropdownOpen] = useState(false);
  const [showStatisticsModal, setShowStatisticsModal] = useState(false);
  const [statistics, setStatistics] = useState(null);
  const [statisticsLoading, setStatisticsLoading] = useState(false);

  // Sync state with URL params when they change
  useEffect(() => {
    if (initialPage !== page) setPage(initialPage);
  }, [initialPage, page]);

  useEffect(() => {
    if (initialFilter !== filter) setFilter(initialFilter);
  }, [initialFilter, filter]);

  useEffect(() => {
    if (initialShowDiscrepanciesOnly !== showDiscrepanciesOnly) setShowDiscrepanciesOnly(initialShowDiscrepanciesOnly);
  }, [initialShowDiscrepanciesOnly, showDiscrepanciesOnly]);

  useEffect(() => {
    if (initialSortByDiscrepancy !== sortByDiscrepancy) setSortByDiscrepancy(initialSortByDiscrepancy);
  }, [initialSortByDiscrepancy, sortByDiscrepancy]);

  useEffect(() => {
    if (JSON.stringify(initialDocumentIds.sort()) !== JSON.stringify(selectedDocumentIds.sort())) {
      setSelectedDocumentIds(initialDocumentIds);
    }
  }, [initialDocumentIds, selectedDocumentIds]);

  // Fetch documents list
  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        const res = await axios.get(`${API_URL}/documents`);
        setDocuments(res.data || []);
      } catch (e) {
        console.error('Failed to fetch documents:', e);
      }
    };
    fetchDocuments();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isDocumentDropdownOpen && !event.target.closest('.document-filter-dropdown')) {
        setIsDocumentDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDocumentDropdownOpen]);

  // Check if user is view-only
  const isViewOnly = user?.role === 'viewer';

  // Helper function to check if a language group should be hidden based on source_language
  const shouldHideLanguageGroup = (sourceLang, languageGroup) => {
    if (!sourceLang) return false;
    const lang = sourceLang.toLowerCase();
    if (languageGroup === 'en' && (lang === 'en' || lang.startsWith('en'))) return true;
    if (languageGroup === 'cn' && (lang === 'zh-cn' || lang === 'zh-tw' || lang === 'zh' || lang.startsWith('zh'))) return true;
    if (languageGroup === 'vi' && (lang === 'vi' || lang.startsWith('vi'))) return true;
    return false;
  };

  // Fetch model configuration
  useEffect(() => {
    const fetchModelConfig = async () => {
      try {
        const response = await axios.get(`${API_URL}/config/models`);
        setModelConfig(response.data);
      } catch (error) {
        console.error("Error fetching model config:", error);
      }
    };
    fetchModelConfig();
  }, []);

  const fetchTerms = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = {
        page: page,
        page_size: 10,
        status: filter,
        show_discrepancies_only: showDiscrepanciesOnly,
        sort_by_discrepancy: sortByDiscrepancy,
      };
      if (selectedDocumentIds.length > 0) {
        params.document_ids = selectedDocumentIds.join(',');
      }
      const response = await axios.get(`${API_URL}/terms`, { params });
      setTerms(response.data.terms);
      setTotalPages(response.data.total_pages);
      setTotalCount(response.data.total_count);
    } catch (error) {
      console.error("Error fetching terms:", error);
      showToast(t('review.couldNotFetchTerms', 'Could not fetch terms.'), "error");
    } finally {
      setIsLoading(false);
    }
  }, [page, filter, showDiscrepanciesOnly, sortByDiscrepancy, selectedDocumentIds, t]);

  useEffect(() => {
    fetchTerms();
  }, [fetchTerms]);

  const runSearch = async () => {
    const q = searchQuery.trim();
    if (!q) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    try {
      const response = await axios.get(`${API_URL}/search`, {
        params: {
          q,
          top_k: 20,
          status: filter,
        },
      });
      setSearchResults(response.data.terms || []);
    } catch (error) {
      console.error("Error searching terms:", error);
      showToast(t('review.searchFailed', 'Search failed.'), "error");
    } finally {
      setSearching(false);
    }
  };

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    setPage(1); // Reset to first page
    if (onFilterChange) {
      onFilterChange(newFilter);
    }
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    if (onPageChange) {
      onPageChange(newPage);
    }
  };

  const handleShowDiscrepanciesOnlyChange = (value) => {
    setShowDiscrepanciesOnly(value);
    setPage(1); // Reset to first page
    if (onShowDiscrepanciesOnlyChange) {
      onShowDiscrepanciesOnlyChange(value);
    }
  };

  const handleSortByDiscrepancyChange = (value) => {
    setSortByDiscrepancy(value);
    setPage(1); // Reset to first page
    if (onSortByDiscrepancyChange) {
      onSortByDiscrepancyChange(value);
    }
  };

  const handleDocumentIdsChange = (newDocumentIds) => {
    setSelectedDocumentIds(newDocumentIds);
    setPage(1); // Reset to first page
    if (onDocumentIdsChange) {
      onDocumentIdsChange(newDocumentIds);
    }
  };

  const toggleDocumentSelection = (documentId) => {
    const newIds = selectedDocumentIds.includes(documentId)
      ? selectedDocumentIds.filter(id => id !== documentId)
      : [...selectedDocumentIds, documentId];
    handleDocumentIdsChange(newIds);
  };

  const handleExport = async (format) => {
    try {
      const response = await axios.get(`${API_URL}/export`, {
        params: { format },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      const filename = format === 'csv' ? 'dataset.csv' : 'dataset.xlsx';
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      showToast(t('review.exportSuccess', { filename: filename }), "success");
    } catch (error) {
      console.error("Error exporting data:", error);
      showToast(t('review.exportFailed', 'Export failed. No reviewed data?'), "error");
    }
  };

  const handleFineTuningExport = async () => {
    try {
      const response = await axios.get(`${API_URL}/export/fine-tuning-data`, {
        params: { target_lang: 'zh' }, // Default to zh
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'fine_tuning_data.jsonl');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      showToast(t('review.exportSuccess', { filename: 'fine_tuning_data.jsonl' }), "success");
    } catch (error) {
      console.error("Error exporting fine-tuning data:", error);
      showToast(t('review.exportFailed', 'Export failed.'), "error");
    }
  };

  const handleShowStatistics = async () => {
    setShowStatisticsModal(true);
    setStatisticsLoading(true);
    setStatistics(null);
    try {
      const response = await axios.get(`${API_URL}/export/dataset-statistics`, {
        params: { target_lang: 'zh' } // Default to zh
      });
      setStatistics(response.data);
    } catch (error) {
      console.error("Error fetching statistics:", error);
      showToast('Failed to load statistics', "error");
      setShowStatisticsModal(false);
    } finally {
      setStatisticsLoading(false);
    }
  };

  const handleDeleteTerm = async (termId) => {
    try {
      await axios.delete(`${API_URL}/terms/${termId}`);
      showToast(t('review.termDeleted', 'Term deleted successfully'), "success");
      // Refresh terms list
      fetchTerms();
      // If there's a search query, refresh search results too
      if (searchQuery.trim()) {
        runSearch();
      }
    } catch (error) {
      console.error("Error deleting term:", error);
      showToast(t('review.deleteFailed', 'Failed to delete term'), "error");
    }
  };

  const handleRetryEmptyExpertTranslations = async () => {
    setIsRetrying(true);
    setRetryTaskStatus(null);
    try {
      const response = await axios.post(`${API_URL}/terms/retry-empty-expert-translations`, null, {
        params: { batch_size: 100 }
      });
      const taskId = response.data.task_id;
      showToast(t('review.retryTaskStarted', 'Retry task started'), "success");

      // Poll for task status
      const pollStatus = async () => {
        try {
          const statusResponse = await axios.get(`${API_URL}/terms/retry-empty-expert-translations/status/${taskId}`);
          setRetryTaskStatus(statusResponse.data);

          if (statusResponse.data.status === 'processing') {
            // Continue polling
            setTimeout(pollStatus, 2000);
          } else if (statusResponse.data.status === 'completed') {
            setIsRetrying(false);
            const result = statusResponse.data.result;
            showToast(
              t('review.retryTaskCompleted', `Retry completed: ${result.successful} successful, ${result.failed} failed`),
              result.failed > 0 ? "error" : "success"
            );
            // Refresh terms list
            fetchTerms();
          } else if (statusResponse.data.status === 'failed') {
            setIsRetrying(false);
            showToast(t('review.retryTaskFailed', 'Retry task failed'), "error");
          }
        } catch (error) {
          console.error("Error polling task status:", error);
          setIsRetrying(false);
        }
      };

      // Start polling after a short delay
      setTimeout(pollStatus, 1000);
    } catch (error) {
      console.error("Error starting retry task:", error);
      showToast(t('review.retryTaskStartFailed', 'Failed to start retry task'), "error");
      setIsRetrying(false);
    }
  };

  // Memoize majority vote calculation for search results
  const searchLanguageHiding = useMemo(() => {
    if (searchResults.length === 0) {
      return { commonLang: null, hideEn: false, hideCn: false, hideVi: false };
    }
    const sourceLangs = searchResults.map(t => t.source_language).filter(Boolean);
    let commonLang = null;
    if (sourceLangs.length > 0) {
      const langCounts = {};
      sourceLangs.forEach(lang => {
        langCounts[lang] = (langCounts[lang] || 0) + 1;
      });
      const mostCommonLang = Object.keys(langCounts).reduce((a, b) =>
        langCounts[a] > langCounts[b] ? a : b
      );
      const majorityThreshold = sourceLangs.length * 0.5;
      commonLang = langCounts[mostCommonLang] > majorityThreshold ? mostCommonLang : null;
    }
    return {
      commonLang,
      hideEn: commonLang && shouldHideLanguageGroup(commonLang, 'en'),
      hideCn: commonLang && shouldHideLanguageGroup(commonLang, 'cn'),
      hideVi: commonLang && shouldHideLanguageGroup(commonLang, 'vi'),
    };
  }, [searchResults]);

  // Memoize majority vote calculation for regular terms
  const termsLanguageHiding = useMemo(() => {
    if (terms.length === 0) {
      return { commonLang: null, hideEn: false, hideCn: false, hideVi: false };
    }
    const sourceLangs = terms.map(t => t.source_language).filter(Boolean);
    let commonLang = null;
    if (sourceLangs.length > 0) {
      const langCounts = {};
      sourceLangs.forEach(lang => {
        langCounts[lang] = (langCounts[lang] || 0) + 1;
      });
      const mostCommonLang = Object.keys(langCounts).reduce((a, b) =>
        langCounts[a] > langCounts[b] ? a : b
      );
      const majorityThreshold = sourceLangs.length * 0.5;
      commonLang = langCounts[mostCommonLang] > majorityThreshold ? mostCommonLang : null;
    }
    return {
      commonLang,
      hideEn: commonLang && shouldHideLanguageGroup(commonLang, 'en'),
      hideCn: commonLang && shouldHideLanguageGroup(commonLang, 'cn'),
      hideVi: commonLang && shouldHideLanguageGroup(commonLang, 'vi'),
    };
  }, [terms]);

  return (
    <div className="p-6 md:p-10">
      {/* Header Section */}
      <div className="mb-6">
        <div className="mb-4">
          <h2 className="text-xl font-semibold text-gray-700">{t('review.title')}</h2>
          {searchQuery.trim() ? (
            <p className="text-sm text-gray-500 mt-1">{t('review.searchResults')}: {searchResults.length}</p>
          ) : (
            <p className="text-sm text-gray-500 mt-1">{totalCount} {t('review.termsFound')}</p>
          )}
        </div>

        {/* Filters and Controls Card */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
          {/* Status Filters Row */}
          <div className="px-4 py-3 border-b border-gray-200">
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-sm font-medium text-gray-700 whitespace-nowrap">{t('review.status', 'Status')}:</span>
              <div className="flex items-center gap-2">
                <FilterButton label={t('review.all')} filter="all" activeFilter={filter} onClick={handleFilterChange} />
                <FilterButton label={t('review.unreviewed')} filter="unreviewed" activeFilter={filter} onClick={handleFilterChange} />
                <FilterButton label={t('review.reviewed')} filter="reviewed" activeFilter={filter} onClick={handleFilterChange} />
              </div>
            </div>
          </div>

          {/* Document Filter and Options Row */}
          <div className="px-4 py-3 border-b border-gray-200">
            <div className="flex flex-wrap items-center gap-4">
              {/* Document Filter */}
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <span className="text-sm font-medium text-gray-700 whitespace-nowrap">{t('review.filterBy', 'Filter by')}:</span>
                <div className="relative document-filter-dropdown">
                  <button
                    onClick={() => setIsDocumentDropdownOpen(!isDocumentDropdownOpen)}
                    className="px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition-colors flex items-center space-x-2"
                  >
                    <FileText className="h-4 w-4 flex-shrink-0" />
                    <span className="truncate">
                      {selectedDocumentIds.length === 0
                        ? t('review.filterByDocument', 'Filter by Document')
                        : `${selectedDocumentIds.length} ${selectedDocumentIds.length === 1 ? t('review.document', 'document') : t('review.documents', 'documents')}`
                      }
                    </span>
                    {selectedDocumentIds.length > 0 && (
                      <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full flex-shrink-0">
                        {selectedDocumentIds.length}
                      </span>
                    )}
                  </button>
                  {isDocumentDropdownOpen && (
                    <div className="absolute z-50 mt-1 w-64 bg-white border border-gray-300 rounded-md shadow-lg max-h-96 overflow-y-auto">
                      <div className="p-2">
                        <div className="flex items-center justify-between mb-2 px-2">
                          <span className="text-sm font-medium text-gray-700">{t('review.selectDocuments', 'Select Documents')}</span>
                          {selectedDocumentIds.length > 0 && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDocumentIdsChange([]);
                              }}
                              className="text-xs text-blue-600 hover:text-blue-800"
                            >
                              {t('common.clear', 'Clear')}
                            </button>
                          )}
                        </div>
                        {documents.length === 0 ? (
                          <div className="px-2 py-4 text-sm text-gray-500 text-center">{t('common.loading', 'Loading...')}</div>
                        ) : (
                          <div className="space-y-1">
                            {documents.map((doc) => (
                              <label
                                key={doc.id}
                                className="flex items-center space-x-2 px-2 py-1.5 hover:bg-gray-50 rounded cursor-pointer"
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedDocumentIds.includes(doc.id)}
                                  onChange={() => toggleDocumentSelection(doc.id)}
                                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-sm text-gray-700 flex-1 truncate">{doc.filename}</span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Discrepancy Controls */}
              <div className="flex items-center gap-4 flex-wrap">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showDiscrepanciesOnly}
                    onChange={(e) => handleShowDiscrepanciesOnlyChange(e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700 whitespace-nowrap">{t('review.showDiscrepanciesOnly')}</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={sortByDiscrepancy}
                    onChange={(e) => handleSortByDiscrepancyChange(e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700 whitespace-nowrap">{t('review.sortByDiscrepancy')}</span>
                </label>
              </div>
            </div>
          </div>

          {/* Actions Row */}
          <div className="px-4 py-3">
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-sm font-medium text-gray-700 whitespace-nowrap">{t('review.actions', 'Actions')}:</span>
              <div className="flex items-center gap-2 flex-wrap">
                {/* Export Buttons */}
                <ExportButton format="csv" onClick={handleExport} />
                <ExportButton format="excel" onClick={handleExport} />
                <ExportButton format="jsonl" onClick={handleFineTuningExport} />
                
                {/* Statistics Button */}
                <button
                  onClick={handleShowStatistics}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  title="View dataset statistics"
                >
                  <BarChart3 className="h-5 w-5 mr-2" />
                  Statistics
                </button>

                {/* Retry Empty Expert Translations */}
                {!isViewOnly && (
                  <>
                    <div className="h-6 w-px bg-gray-300"></div>
                    <button
                      onClick={handleRetryEmptyExpertTranslations}
                      disabled={isRetrying}
                      className="px-3 py-1.5 text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                      title={t('review.retryEmptyExpertTranslations', 'Retry Expert translations for terms with empty Expert fields')}
                    >
                      {isRetrying ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          <span>{t('review.retrying', 'Retrying...')}</span>
                        </>
                      ) : (
                        <>
                          <Pencil className="h-4 w-4" />
                          <span>{t('review.retryEmptyExpert', 'Retry Empty Expert')}</span>
                        </>
                      )}
                    </button>
                    {retryTaskStatus && retryTaskStatus.status === 'processing' && (
                      <span className="text-xs text-gray-600 ml-2">
                        Processing...
                      </span>
                    )}
                    {retryTaskStatus && retryTaskStatus.status === 'completed' && retryTaskStatus.result && (
                      <span className="text-xs text-green-600 ml-2">
                        ✓ {retryTaskStatus.result.successful || 0} successful, {retryTaskStatus.result.failed || 0} failed
                      </span>
                    )}
                    {retryTaskStatus && retryTaskStatus.status === 'failed' && (
                      <span className="text-xs text-red-600 ml-2">
                        ✗ Failed
                      </span>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="mb-4 flex items-center space-x-2">
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') runSearch(); }}
          placeholder={t('review.semanticSearch')}
          className="flex-1 p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
        />
        <button
          onClick={runSearch}
          disabled={searching}
          className="px-4 py-2 rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
        >
          {searching ? t('review.searching') : t('common.search')}
        </button>
        {searchQuery.trim() && (
          <button
            onClick={() => { setSearchQuery(""); setSearchResults([]); }}
            className="px-3 py-2 rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
          >
            {t('common.clear')}
          </button>
        )}
      </div>

      {/* Terms Table */}
      <div className="overflow-x-auto shadow border-b border-gray-200 rounded-lg w-full">
        {searchQuery.trim() ? (
          searching ? (
            <div className="flex justify-center items-center h-64">
              <Loader2 className="h-10 w-10 text-blue-600 animate-spin" />
            </div>
          ) : searchResults.length === 0 ? (
            <p className="text-center py-10 text-gray-500">{t('review.noResults')}</p>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/5">{t('review.original')}</th>
                  {!searchLanguageHiding.hideEn && (
                    <>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <div className="flex items-center gap-1 group relative">
                          {t('review.expertEn')}
                          <Info className="h-3 w-3 text-gray-400 cursor-help" />
                          <span className="absolute left-0 top-full mt-1 w-64 p-2 bg-gray-900 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none">
                            {t('review.model')} {modelConfig.expert_model || t('common.loading')}
                          </span>
                        </div>
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <div className="flex items-center gap-1 group relative">
                          {t('review.expert2En')}
                          <Info className="h-3 w-3 text-gray-400 cursor-help" />
                          <span className="absolute left-0 top-full mt-1 w-64 p-2 bg-gray-900 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none">
                            {t('review.model')} {modelConfig.expert_2_model || t('common.loading')}
                          </span>
                        </div>
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <div className="flex items-center gap-1 group relative">
                          {t('review.customEn')}
                          <Info className="h-3 w-3 text-gray-400 cursor-help" />
                          <span className="absolute left-0 top-full mt-1 w-64 p-2 bg-gray-900 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none">
                            {t('review.model')} {modelConfig.custom_model || t('common.loading')}
                          </span>
                        </div>
                      </th>
                    </>
                  )}
                  {!searchLanguageHiding.hideCn && (
                    <>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <div className="flex items-center gap-1 group relative">
                          {t('review.expertCn')}
                          <Info className="h-3 w-3 text-gray-400 cursor-help" />
                          <span className="absolute left-0 top-full mt-1 w-64 p-2 bg-gray-900 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none">
                            {t('review.model')} {modelConfig.expert_model || t('common.loading')}
                          </span>
                        </div>
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <div className="flex items-center gap-1 group relative">
                          {t('review.expert2Cn')}
                          <Info className="h-3 w-3 text-gray-400 cursor-help" />
                          <span className="absolute left-0 top-full mt-1 w-64 p-2 bg-gray-900 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none">
                            {t('review.model')} {modelConfig.expert_2_model || t('common.loading')}
                          </span>
                        </div>
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <div className="flex items-center gap-1 group relative">
                          {t('review.customCn')}
                          <Info className="h-3 w-3 text-gray-400 cursor-help" />
                          <span className="absolute left-0 top-full mt-1 w-64 p-2 bg-gray-900 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none">
                            {t('review.model')} {modelConfig.custom_model || t('common.loading')}
                          </span>
                        </div>
                      </th>
                    </>
                  )}
                  {!searchLanguageHiding.hideVi && (
                    <>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <div className="flex items-center gap-1 group relative">
                          {t('review.expertVi')}
                          <Info className="h-3 w-3 text-gray-400 cursor-help" />
                          <span className="absolute left-0 top-full mt-1 w-64 p-2 bg-gray-900 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none">
                            {t('review.model')} {modelConfig.expert_model || t('common.loading')}
                          </span>
                        </div>
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <div className="flex items-center gap-1 group relative">
                          {t('review.expert2Vi')}
                          <Info className="h-3 w-3 text-gray-400 cursor-help" />
                          <span className="absolute left-0 top-full mt-1 w-64 p-2 bg-gray-900 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none">
                            {t('review.model')} {modelConfig.expert_2_model || t('common.loading')}
                          </span>
                        </div>
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <div className="flex items-center gap-1 group relative">
                          {t('review.customVi')}
                          <Info className="h-3 w-3 text-gray-400 cursor-help" />
                          <span className="absolute left-0 top-full mt-1 w-64 p-2 bg-gray-900 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none">
                            {t('review.model')} {modelConfig.custom_model || t('common.loading')}
                          </span>
                        </div>
                      </th>
                    </>
                  )}
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('review.similarity')}</th>
                  <th scope="col" className="relative px-6 py-3"><span className="sr-only">{t('review.actions')}</span></th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {searchResults.map((term) => (
                  <TermRow
                    key={term.id}
                    term={term}
                    isEditing={editingId === term.id}
                    onEdit={() => setEditingId(term.id)}
                    onCancel={() => setEditingId(null)}
                    onSaveSuccess={() => { setEditingId(null); fetchTerms(); runSearch(); }}
                    onDelete={handleDeleteTerm}
                    hideEn={searchLanguageHiding.hideEn}
                    hideCn={searchLanguageHiding.hideCn}
                    hideVi={searchLanguageHiding.hideVi}
                    isViewOnly={isViewOnly}
                  />
                ))}
              </tbody>
            </table>
          )
        ) : isLoading && terms.length === 0 ? (
          <div className="flex justify-center items-center h-64">
            <Loader2 className="h-10 w-10 text-blue-600 animate-spin" />
          </div>
        ) : terms.length === 0 ? (
          <p className="text-center py-10 text-gray-500">{t('review.noTerms')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/5">{t('review.original')}</th>
                  {!termsLanguageHiding.hideEn && (
                    <>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <div className="flex items-center gap-1 group relative">
                          {t('review.expertEn')}
                          <Info className="h-3 w-3 text-gray-400 cursor-help" />
                          <span className="absolute left-0 top-full mt-1 w-64 p-2 bg-gray-900 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none">
                            {t('review.model')} {modelConfig.expert_model || t('common.loading')}
                          </span>
                        </div>
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <div className="flex items-center gap-1 group relative">
                          {t('review.expert2En')}
                          <Info className="h-3 w-3 text-gray-400 cursor-help" />
                          <span className="absolute left-0 top-full mt-1 w-64 p-2 bg-gray-900 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none">
                            {t('review.model')} {modelConfig.expert_2_model || t('common.loading')}
                          </span>
                        </div>
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <div className="flex items-center gap-1 group relative">
                          {t('review.customEn')}
                          <Info className="h-3 w-3 text-gray-400 cursor-help" />
                          <span className="absolute left-0 top-full mt-1 w-64 p-2 bg-gray-900 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none">
                            {t('review.model')} {modelConfig.custom_model || t('common.loading')}
                          </span>
                        </div>
                      </th>
                    </>
                  )}
                  {!termsLanguageHiding.hideCn && (
                    <>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <div className="flex items-center gap-1 group relative">
                          {t('review.expertCn')}
                          <Info className="h-3 w-3 text-gray-400 cursor-help" />
                          <span className="absolute left-0 top-full mt-1 w-64 p-2 bg-gray-900 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none">
                            {t('review.model')} {modelConfig.expert_model || t('common.loading')}
                          </span>
                        </div>
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <div className="flex items-center gap-1 group relative">
                          {t('review.expert2Cn')}
                          <Info className="h-3 w-3 text-gray-400 cursor-help" />
                          <span className="absolute left-0 top-full mt-1 w-64 p-2 bg-gray-900 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none">
                            {t('review.model')} {modelConfig.expert_2_model || t('common.loading')}
                          </span>
                        </div>
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <div className="flex items-center gap-1 group relative">
                          {t('review.customCn')}
                          <Info className="h-3 w-3 text-gray-400 cursor-help" />
                          <span className="absolute left-0 top-full mt-1 w-64 p-2 bg-gray-900 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none">
                            {t('review.model')} {modelConfig.custom_model || t('common.loading')}
                          </span>
                        </div>
                      </th>
                    </>
                  )}
                  {!termsLanguageHiding.hideVi && (
                    <>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <div className="flex items-center gap-1 group relative">
                          {t('review.expertVi')}
                          <Info className="h-3 w-3 text-gray-400 cursor-help" />
                          <span className="absolute left-0 top-full mt-1 w-64 p-2 bg-gray-900 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none">
                            {t('review.model')} {modelConfig.expert_model || t('common.loading')}
                          </span>
                        </div>
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <div className="flex items-center gap-1 group relative">
                          {t('review.expert2Vi')}
                          <Info className="h-3 w-3 text-gray-400 cursor-help" />
                          <span className="absolute left-0 top-full mt-1 w-64 p-2 bg-gray-900 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none">
                            {t('review.model')} {modelConfig.expert_2_model || t('common.loading')}
                          </span>
                        </div>
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <div className="flex items-center gap-1 group relative">
                          {t('review.customVi')}
                          <Info className="h-3 w-3 text-gray-400 cursor-help" />
                          <span className="absolute left-0 top-full mt-1 w-64 p-2 bg-gray-900 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none">
                            {t('review.model')} {modelConfig.custom_model || t('common.loading')}
                          </span>
                        </div>
                      </th>
                    </>
                  )}
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th scope="col" className="relative px-6 py-3"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {terms.map((term) => (
                  <TermRow
                    key={term.id}
                    term={term}
                    isEditing={editingId === term.id}
                    onEdit={() => setEditingId(term.id)}
                    onCancel={() => setEditingId(null)}
                    onSaveSuccess={() => {
                      setEditingId(null);
                      fetchTerms();
                    }}
                    onDelete={handleDeleteTerm}
                    hideEn={termsLanguageHiding.hideEn}
                    isViewOnly={isViewOnly}
                    hideCn={termsLanguageHiding.hideCn}
                    hideVi={termsLanguageHiding.hideVi}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination (hidden during search) */}
      {!searchQuery.trim() && totalPages > 1 && (
        <Pagination
          page={page}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      )}

      {/* Statistics Modal */}
      <StatisticsModal
        isOpen={showStatisticsModal}
        onClose={() => {
          setShowStatisticsModal(false);
          setStatistics(null);
        }}
        statistics={statistics}
        loading={statisticsLoading}
      />
    </div>
  );
}

// --- Review View Sub-components ---

const TermRow = React.memo(function TermRow({ term, isEditing, onEdit, onCancel, onSaveSuccess, onDelete, hideEn = false, hideCn = false, hideVi = false, isViewOnly = false }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [expertEn, setExpertEn] = useState(term.expert_translated_en ?? "");
  const [expert2En, setExpert2En] = useState(term.expert_2_translated_en ?? "");
  const [customEn, setCustomEn] = useState(term.custom_translated_en ?? "");
  const [expertCn, setExpertCn] = useState(term.expert_translated_cn ?? "");
  const [expert2Cn, setExpert2Cn] = useState(term.expert_2_translated_cn ?? "");
  const [customCn, setCustomCn] = useState(term.custom_translated_cn ?? "");
  const [expertVi, setExpertVi] = useState(term.expert_translated_vi ?? "");
  const [expert2Vi, setExpert2Vi] = useState(term.expert_2_translated_vi ?? "");
  const [customVi, setCustomVi] = useState(term.custom_translated_vi ?? "");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setExpertEn(term.expert_translated_en ?? "");
    setExpert2En(term.expert_2_translated_en ?? "");
    setCustomEn(term.custom_translated_en ?? "");
    setExpertCn(term.expert_translated_cn ?? "");
    setExpert2Cn(term.expert_2_translated_cn ?? "");
    setCustomCn(term.custom_translated_cn ?? "");
    setExpertVi(term.expert_translated_vi ?? "");
    setExpert2Vi(term.expert_2_translated_vi ?? "");
    setCustomVi(term.custom_translated_vi ?? "");
  }, [term]);

  // Get discrepancy info
  const discrepancy = term.discrepancy || {
    has_discrepancy: false,
    en_discrepancy: false,
    cn_discrepancy: false,
    en_similarity: 1.0,
    cn_similarity: 1.0,
    discrepancy_score: 0.0,
  };

  // Get expert discrepancy info (Expert vs Expert 2)
  const expertDiscrepancy = term.expert_discrepancy || {
    has_discrepancy: false,
    en_discrepancy: false,
    cn_discrepancy: false,
    en_similarity: 1.0,
    cn_similarity: 1.0,
    discrepancy_score: 0.0,
  };

  // Combined discrepancy check for row highlighting
  const hasAnyDiscrepancy = discrepancy.has_discrepancy || expertDiscrepancy.has_discrepancy;

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await axios.put(`${API_URL}/terms/${term.id}`, {
        expert_translated_en: expertEn,
        expert_translated_cn: expertCn,
        expert_translated_vi: expertVi,
        expert_2_translated_en: expert2En,
        expert_2_translated_cn: expert2Cn,
        expert_2_translated_vi: expert2Vi,
        custom_translated_en: customEn,
        custom_translated_cn: customCn,
        custom_translated_vi: customVi,
      });
      showToast(t('review.saveSuccess'), "success");
      onSaveSuccess();
    } catch (error) {
      console.error("Error saving term:", error);
      showToast(t('review.saveFailed'), "error");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <tr className={`${isEditing ? "bg-blue-50" : ""} ${hasAnyDiscrepancy ? "bg-orange-50 border-l-4 border-l-orange-400" : ""}`}>
      <td className="px-6 py-4 whitespace-normal text-sm text-gray-600 align-top">
        <div className="flex items-start gap-2">
          {hasAnyDiscrepancy && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800" title={`${t('review.discrepancyScore', 'Discrepancy score')}: ${(Math.max(discrepancy.discrepancy_score, expertDiscrepancy.discrepancy_score) * 100).toFixed(1)}%`}>
              ⚠️
            </span>
          )}
          <div>
            {term.original_text}
            <div className="text-xs text-gray-400 mt-1">
              ({t('review.docPage')} {term.document_id}, {t('review.Page:')} {term.page_number})
            </div>
          </div>
        </div>
      </td>
      {!hideEn && (
        <>
          <td className={`px-6 py-4 whitespace-normal text-sm text-gray-900 align-top ${expertDiscrepancy.en_discrepancy ? "bg-red-50" : ""}`}>
            {isEditing ? (
              <textarea
                value={expertEn ?? ""}
                onChange={(e) => setExpertEn(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                rows="3"
              />
            ) : (
              <div>
                {expertDiscrepancy.en_discrepancy && (
                  <span className="inline-block mb-1 px-1.5 py-0.5 text-xs bg-red-100 text-red-700 rounded" title={`${t('review.expertEn')} ${t('review.similarity')}: ${(expertDiscrepancy.en_similarity * 100).toFixed(1)}%`}>
                    {t('review.diff', 'Diff')}
                  </span>
                )}
                <div>{expertEn}</div>
              </div>
            )}
          </td>
          <td className={`px-6 py-4 whitespace-normal text-sm text-gray-900 align-top ${expertDiscrepancy.en_discrepancy ? "bg-red-50" : ""}`}>
            {isEditing ? (
              <textarea
                value={expert2En ?? ""}
                onChange={(e) => setExpert2En(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                rows="3"
              />
            ) : (
              <div>
                {expertDiscrepancy.en_discrepancy && (
                  <span className="inline-block mb-1 px-1.5 py-0.5 text-xs bg-red-100 text-red-700 rounded" title={`${t('review.expert2En')} ${t('review.similarity')}: ${(expertDiscrepancy.en_similarity * 100).toFixed(1)}%`}>
                    {t('review.diff', 'Diff')}
                  </span>
                )}
                <div>{expert2En}</div>
              </div>
            )}
          </td>
          <td className={`px-6 py-4 whitespace-normal text-sm text-gray-900 align-top ${discrepancy.en_discrepancy ? "bg-red-50" : ""}`}>
            {isEditing ? (
              <textarea
                value={customEn ?? ""}
                onChange={(e) => setCustomEn(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                rows="3"
              />
            ) : (
              <div>
                {discrepancy.en_discrepancy && (
                  <span className="inline-block mb-1 px-1.5 py-0.5 text-xs bg-red-100 text-red-700 rounded" title={`${t('review.customEn')} ${t('review.similarity')}: ${(discrepancy.en_similarity * 100).toFixed(1)}%`}>
                    {t('review.diff', 'Diff')}
                  </span>
                )}
                <div>{customEn}</div>
              </div>
            )}
          </td>
        </>
      )}
      {!hideCn && (
        <>
          <td className={`px-6 py-4 whitespace-normal text-sm text-gray-900 align-top ${expertDiscrepancy.cn_discrepancy ? "bg-red-50" : ""}`}>
            {isEditing ? (
              <textarea
                value={expertCn ?? ""}
                onChange={(e) => setExpertCn(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                rows="3"
              />
            ) : (
              <div>
                {expertDiscrepancy.cn_discrepancy && (
                  <span className="inline-block mb-1 px-1.5 py-0.5 text-xs bg-red-100 text-red-700 rounded" title={`${t('review.expertCn')} ${t('review.similarity')}: ${(expertDiscrepancy.cn_similarity * 100).toFixed(1)}%`}>
                    {t('review.diff', 'Diff')}
                  </span>
                )}
                <div>{expertCn}</div>
              </div>
            )}
          </td>
          <td className={`px-6 py-4 whitespace-normal text-sm text-gray-900 align-top ${expertDiscrepancy.cn_discrepancy ? "bg-red-50" : ""}`}>
            {isEditing ? (
              <textarea
                value={expert2Cn ?? ""}
                onChange={(e) => setExpert2Cn(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                rows="3"
              />
            ) : (
              <div>
                {expertDiscrepancy.cn_discrepancy && (
                  <span className="inline-block mb-1 px-1.5 py-0.5 text-xs bg-red-100 text-red-700 rounded" title={`${t('review.expert2Cn')} ${t('review.similarity')}: ${(expertDiscrepancy.cn_similarity * 100).toFixed(1)}%`}>
                    {t('review.diff', 'Diff')}
                  </span>
                )}
                <div>{expert2Cn}</div>
              </div>
            )}
          </td>
          <td className={`px-6 py-4 whitespace-normal text-sm text-gray-900 align-top ${discrepancy.cn_discrepancy ? "bg-red-50" : ""}`}>
            {isEditing ? (
              <textarea
                value={customCn ?? ""}
                onChange={(e) => setCustomCn(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                rows="3"
              />
            ) : (
              <div>
                {discrepancy.cn_discrepancy && (
                  <span className="inline-block mb-1 px-1.5 py-0.5 text-xs bg-red-100 text-red-700 rounded" title={`${t('review.customCn')} ${t('review.similarity')}: ${(discrepancy.cn_similarity * 100).toFixed(1)}%`}>
                    {t('review.diff', 'Diff')}
                  </span>
                )}
                <div>{customCn}</div>
              </div>
            )}
          </td>
        </>
      )}
      {!hideVi && (
        <>
          <td className={`px-6 py-4 whitespace-normal text-sm text-gray-900 align-top ${expertDiscrepancy.cn_discrepancy ? "bg-red-50" : ""}`}>
            {isEditing ? (
              <textarea
                value={expertVi ?? ""}
                onChange={(e) => setExpertVi(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                rows="3"
              />
            ) : (
              <div>
                {expertDiscrepancy.cn_discrepancy && (
                  <span className="inline-block mb-1 px-1.5 py-0.5 text-xs bg-red-100 text-red-700 rounded" title={`${t('review.expertVi')} ${t('review.similarity')}: ${(expertDiscrepancy.cn_similarity * 100).toFixed(1)}%`}>
                    {t('review.diff', 'Diff')}
                  </span>
                )}
                <div>{expertVi}</div>
              </div>
            )}
          </td>
          <td className={`px-6 py-4 whitespace-normal text-sm text-gray-900 align-top ${expertDiscrepancy.cn_discrepancy ? "bg-red-50" : ""}`}>
            {isEditing ? (
              <textarea
                value={expert2Vi ?? ""}
                onChange={(e) => setExpert2Vi(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                rows="3"
              />
            ) : (
              <div>
                {expertDiscrepancy.cn_discrepancy && (
                  <span className="inline-block mb-1 px-1.5 py-0.5 text-xs bg-red-100 text-red-700 rounded" title={`${t('review.expert2Vi')} ${t('review.similarity')}: ${(expertDiscrepancy.cn_similarity * 100).toFixed(1)}%`}>
                    {t('review.diff', 'Diff')}
                  </span>
                )}
                <div>{expert2Vi}</div>
              </div>
            )}
          </td>
          <td className={`px-6 py-4 whitespace-normal text-sm text-gray-900 align-top ${discrepancy.cn_discrepancy ? "bg-red-50" : ""}`}>
            {isEditing ? (
              <textarea
                value={customVi ?? ""}
                onChange={(e) => setCustomVi(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                rows="3"
              />
            ) : (
              <div>
                {discrepancy.cn_discrepancy && (
                  <span className="inline-block mb-1 px-1.5 py-0.5 text-xs bg-red-100 text-red-700 rounded" title={`${t('review.customVi')} ${t('review.similarity')}: ${(discrepancy.cn_similarity * 100).toFixed(1)}%`}>
                    {t('review.diff', 'Diff')}
                  </span>
                )}
                <div>{customVi}</div>
              </div>
            )}
          </td>
        </>
      )}
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 align-top">
        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${term.status === 'reviewed'
          ? 'bg-green-100 text-green-800'
          : 'bg-yellow-100 text-yellow-800'
          }`}>
          {t(`documentStatus.status.${term.status}`, term.status)}
        </span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium align-top">
        {isEditing ? (
          <div className="flex space-x-2">
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="p-2 text-green-600 hover:text-green-900 rounded-md hover:bg-green-100 disabled:opacity-50"
            >
              {isSaving ? <Loader2 className="h-5 w-5 animate-spin" /> : <Save className="h-5 w-5" />}
            </button>
            <button onClick={onCancel} className="p-2 text-gray-500 hover:text-gray-800 rounded-md hover:bg-gray-100">
              <X className="h-5 w-5" />
            </button>
          </div>
        ) : (
          <div className="flex space-x-2 justify-end">
            <button
              onClick={() => navigate(`/term/${term.id}`)}
              className="p-2 text-purple-600 hover:text-purple-900 rounded-md hover:bg-purple-100"
              title={t('termDetails.viewDetails', 'View Details')}
            >
              <Eye className="h-5 w-5" />
            </button>
            {!isViewOnly && (
              <>
                <button onClick={onEdit} className="p-2 text-blue-600 hover:text-blue-900 rounded-md hover:bg-blue-100" title={t('review.edit', 'Edit')}>
                  <Pencil className="h-5 w-5" />
                </button>
                {onDelete && (
                  <button
                    onClick={() => {
                      if (window.confirm(t('review.confirmDelete', `Are you sure you want to delete "${term.original_text}"?`))) {
                        onDelete(term.id);
                      }
                    }}
                    className="p-2 text-red-600 hover:text-red-900 rounded-md hover:bg-red-100"
                    title={t('review.delete', 'Delete')}
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                )}
              </>
            )}
            {isViewOnly && (
              <span className="text-xs text-gray-400 px-2 py-1" title={t('review.viewOnly', 'View-only mode')}>
                {t('review.viewOnly', 'View-only')}
              </span>
            )}
          </div>
        )}
      </td>
    </tr>
  );
});

function FilterButton({ label, filter, activeFilter, onClick }) {
  const isActive = filter === activeFilter;
  return (
    <button
      onClick={() => onClick(filter)}
      className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${isActive
        ? 'bg-blue-600 text-white shadow-sm'
        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
        }`}
    >
      {label}
    </button>
  );
}

function ExportButton({ format, onClick }) {
  const isExcel = format === 'excel';
  const isJsonl = format === 'jsonl';

  let bgClass = 'bg-gray-600 hover:bg-gray-700 focus:ring-gray-500';
  if (isExcel) bgClass = 'bg-green-600 hover:bg-green-700 focus:ring-green-500';
  if (isJsonl) bgClass = 'bg-purple-600 hover:bg-purple-700 focus:ring-purple-500';

  let label = 'CSV';
  if (isExcel) label = 'Excel';
  if (isJsonl) label = 'JSONL';

  return (
    <button
      onClick={() => onClick(format)}
      className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${bgClass} focus:outline-none focus:ring-2 focus:ring-offset-2`}
    >
      <Download className="h-5 w-5 mr-2" />
      {label}
    </button>
  );
}

function Pagination({ page, totalPages, onPageChange }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 mt-4 rounded-b-lg">
      <div className="flex-1 flex justify-between sm:hidden">
        <button
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
        >
          {t('common.previous')}
        </button>
        <button
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
          className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
        >
          {t('common.next')}
        </button>
      </div>
      <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
        <div>
          <p className="text-sm text-gray-700">
            {t('common.page')} <span className="font-medium">{page}</span> {t('common.of')} <span className="font-medium">{totalPages}</span>
          </p>
        </div>
        <div>
          <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
            <button
              onClick={() => onPageChange(page - 1)}
              disabled={page <= 1}
              className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
            >
              <span className="sr-only">{t('common.previous')}</span>
              <ChevronLeft className="h-5 w-5" />
            </button>
            <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
              {page}
            </span>
            <button
              onClick={() => onPageChange(page + 1)}
              disabled={page >= totalPages}
              className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
            >
              <span className="sr-only">{t('common.next')}</span>
              <ChevronRight className="h-5 w-5" />
            </button>
          </nav>
        </div>
      </div>
    </div>
  );
}

// --- Toast Notification System ---
// Simple global state for toast messages
let toastTimeout;
function showToast(message, type = 'success') {
  const toastEl = document.getElementById('toast-notification');
  if (!toastEl) return;

  const iconEl = toastEl.querySelector('#toast-icon');
  const messageEl = toastEl.querySelector('#toast-message');

  // Reset classes
  toastEl.classList.remove('bg-green-500', 'bg-red-500', 'translate-y-20', 'opacity-0');

  if (type === 'success') {
    toastEl.classList.add('bg-green-500');
    iconEl.innerHTML = `<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />`;
  } else {
    toastEl.classList.add('bg-red-500');
    iconEl.innerHTML = `<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />`;
  }

  messageEl.textContent = message;
  toastEl.classList.remove('hidden');

  if (toastTimeout) clearTimeout(toastTimeout);
  toastTimeout = setTimeout(() => {
    toastEl.classList.add('translate-y-20', 'opacity-0');
    setTimeout(() => toastEl.classList.add('hidden'), 300); // Hide after transition
  }, 3000);
}

function Toaster() {
  return (
    <div
      id="toast-notification"
      className="hidden fixed bottom-5 right-5 flex items-center w-full max-w-xs p-4 space-x-4 text-white rounded-lg shadow transition-all duration-300 transform"
      role="alert"
    >
      <div className="inline-flex items-center justify-center flex-shrink-0 w-8 h-8 rounded-lg">
        <svg id="toast-icon" className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"></svg>
      </div>
      <div id="toast-message" className="ml-3 text-sm font-medium"></div>
    </div>
  );
}

// --- Documentation View Component ---
// Wrapper component for route-based DocsView
function DocsViewWithRoute() {
  return (
    <>
      <NavBar view="docs" setView={() => { }} />
      <DocsView />
    </>
  );
}

function DocsView() {
  const { t } = useTranslation();

  return (
    <div className="p-6 md:p-10 max-w-4xl mx-auto">
      {/* Language Switcher - Note: Language is now controlled globally via header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800">{t('docs.title')}</h1>
      </div>

      {/* Content Sections */}
      <div className="space-y-8">
        {/* Overview */}
        <section className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">{t('docs.overview.title')}</h2>
          <p className="text-gray-700 leading-relaxed">{t('docs.overview.content')}</p>
        </section>

        {/* Getting Started */}
        <section className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">{t('docs.gettingStarted.title')}</h2>
          <ol className="list-decimal list-inside space-y-3 text-gray-700">
            <li className="leading-relaxed">{t('docs.gettingStarted.step1')}</li>
            <li className="leading-relaxed">{t('docs.gettingStarted.step2')}</li>
            <li className="leading-relaxed">{t('docs.gettingStarted.step3')}</li>
          </ol>
        </section>

        {/* Review Interface */}
        <section className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">{t('docs.reviewInterface.title')}</h2>
          <p className="text-gray-700 mb-4 leading-relaxed">{t('docs.reviewInterface.content')}</p>
          <ul className="list-disc list-inside space-y-2 mb-4 text-gray-700">
            <li>{t('docs.reviewInterface.provider1')}</li>
            <li>{t('docs.reviewInterface.provider2')}</li>
            <li>{t('docs.reviewInterface.provider3')}</li>
          </ul>
          <p className="text-gray-700 mb-4 leading-relaxed">{t('docs.reviewInterface.languages')}</p>
          <p className="text-gray-700 leading-relaxed">{t('docs.reviewInterface.editing')}</p>
        </section>

        {/* Filters and Search */}
        <section className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">{t('docs.filters.title')}</h2>
          <div className="mb-4">
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>{t('docs.filters.filter1')}</li>
              <li>{t('docs.filters.filter2')}</li>
              <li>{t('docs.filters.filter3')}</li>
            </ul>
          </div>
          <p className="text-gray-700 mb-4 leading-relaxed">{t('docs.filters.search')}</p>
          <p className="text-gray-700 leading-relaxed">{t('docs.filters.discrepancies')}</p>
        </section>

        {/* Discrepancy Detection */}
        <section className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">{t('docs.discrepancyDetection.title')}</h2>
          <p className="text-gray-700 mb-4 leading-relaxed">{t('docs.discrepancyDetection.content')}</p>
          <ul className="list-disc list-inside space-y-2 mb-4 text-gray-700">
            <li>{t('docs.discrepancyDetection.type1')}</li>
            <li>{t('docs.discrepancyDetection.type2')}</li>
          </ul>
          <p className="text-gray-700 mb-3 leading-relaxed">{t('docs.discrepancyDetection.indicators')}</p>
          <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
            <li>{t('docs.discrepancyDetection.indicator1')}</li>
            <li>{t('docs.discrepancyDetection.indicator2')}</li>
            <li>{t('docs.discrepancyDetection.indicator3')}</li>
            <li>{t('docs.discrepancyDetection.indicator4')}</li>
            <li>{t('docs.discrepancyDetection.indicator5')}</li>
          </ul>
        </section>

        {/* Export */}
        <section className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">{t('docs.export.title')}</h2>
          <p className="text-gray-700 leading-relaxed">{t('docs.export.content')}</p>
        </section>

        {/* Tips */}
        <section className="bg-blue-50 p-6 rounded-lg shadow-sm border border-blue-200">
          <h2 className="text-2xl font-semibold text-blue-800 mb-4">{t('docs.tips.title')}</h2>
          <ul className="list-disc list-inside space-y-2 text-gray-700">
            <li className="leading-relaxed">{t('docs.tips.tip1')}</li>
            <li className="leading-relaxed">{t('docs.tips.tip2')}</li>
            <li className="leading-relaxed">{t('docs.tips.tip3')}</li>
            <li className="leading-relaxed">{t('docs.tips.tip4')}</li>
            <li className="leading-relaxed">{t('docs.tips.tip5')}</li>
          </ul>
        </section>
      </div>
    </div>
  );
}

// Component to load page image with authentication
function PageImage({ pageId, pageNumber }) {
  const { t } = useTranslation();
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const loadImage = async () => {
      try {
        setLoading(true);
        setError(false);
        const response = await axios.get(`${API_URL}/pages/${pageId}/image`, {
          responseType: 'blob',
        });
        const blob = new Blob([response.data], { type: 'image/png' });
        const url = URL.createObjectURL(blob);
        setImageUrl(url);
      } catch (err) {
        console.error('Failed to load page image:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    if (pageId) {
      loadImage();
    }

    return () => {
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [pageId, imageUrl]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 bg-gray-50 rounded">
        <Loader2 className="h-8 w-8 text-gray-400 animate-spin" />
      </div>
    );
  }

  if (error || !imageUrl) {
    return (
      <div className="flex items-center justify-center h-32 bg-gray-50 rounded text-sm text-gray-500">
        {t('termDetails.imageNotAvailable', 'Image not available')}
      </div>
    );
  }

  return (
    <img
      src={imageUrl}
      alt={`Page ${pageNumber || ''}`}
      className="max-w-full h-auto rounded shadow-sm cursor-pointer hover:opacity-90 transition-opacity"
      onClick={() => {
        const w = window.open('');
        w.document.write(`<img src="${imageUrl}" style="max-width:100%; height:auto;" />`);
      }}
    />
  );
}

// --- Term Details View ---
function TermDetailsView({ user }) {
  const { t } = useTranslation();
  const { termId } = useParams();
  const navigate = useNavigate();
  const [term, setTerm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modelConfig, setModelConfig] = useState({});
  const [retrying, setRetrying] = useState(false);
  const [fetchingAdditional, setFetchingAdditional] = useState(false);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [generatingExplanation, setGeneratingExplanation] = useState(false);
  const [copiedPromptKey, setCopiedPromptKey] = useState(null);
  const [selectedExplanationModel, setSelectedExplanationModel] = useState(null);
  const [availableExplanationModels, setAvailableExplanationModels] = useState([]);
  const [streamingExplanation, setStreamingExplanation] = useState(null);

  const fetchTerm = useCallback(async () => {
    try {
      setLoading(true);
      // Add cache-busting timestamp to ensure fresh data
      const timestamp = new Date().getTime();
      const [termRes, configRes] = await Promise.all([
        axios.get(`${API_URL}/terms/${termId}`, {
          params: { _t: timestamp },
          headers: { 'Cache-Control': 'no-cache' }
        }),
        axios.get(`${API_URL}/config/models`)
      ]);
      setTerm(termRes.data);
      setModelConfig(configRes.data);
      // Set available explanation models and default selected model
      if (configRes.data.available_explanation_models) {
        setAvailableExplanationModels(configRes.data.available_explanation_models);
        // Set default to first model if not already set (using functional form to avoid dependency)
        setSelectedExplanationModel(prev => prev || configRes.data.available_explanation_models[0] || configRes.data.expert_model);
      }
      setError(null);
      // Debug log
      console.log('Fetched term data:', {
        termId: termRes.data.id,
        additionalTranslationsCount: termRes.data.additional_translations ? Object.keys(termRes.data.additional_translations).length : 0,
        additionalTranslations: termRes.data.additional_translations
      });
    } catch (err) {
      console.error('Error fetching term:', err);
      setError(err.response?.data?.detail || 'Failed to load term details');
    } finally {
      setLoading(false);
    }
  }, [termId]);

  useEffect(() => {
    if (termId) {
      fetchTerm();
    }
  }, [termId, fetchTerm]);

  const handleRetryTranslation = async () => {
    if (!termId || retrying) return;

    try {
      setRetrying(true);
      await axios.post(`${API_URL}/terms/${termId}/retry`, {
        retranslate: true,
        reembed: false
      });
      showToast(t('termDetails.retrySuccess', 'Translation retry successful'), 'success');
      // Refresh term data after retry
      await fetchTerm();
    } catch (err) {
      console.error('Error retrying translation:', err);
      showToast(err.response?.data?.detail || t('termDetails.retryFailed', 'Failed to retry translation'), 'error');
    } finally {
      setRetrying(false);
    }
  };

  const handleFetchAdditionalTranslations = async () => {
    if (!termId || fetchingAdditional) return;

    try {
      setFetchingAdditional(true);
      const response = await axios.post(`${API_URL}/terms/${termId}/translate-with-models`, {});
      showToast(
        t('termDetails.additionalTranslationsSuccess', 'Fetched {{count}} additional translations', { count: response.data.models_translated }),
        'success'
      );
      // Small delay to ensure backend has committed the transaction
      await new Promise(resolve => setTimeout(resolve, 300));
      // Refresh term data to show new translations - force refresh by clearing term first
      setTerm(null);
      await fetchTerm();
      // Log for debugging
      console.log('Refreshed term data after fetching translations');
    } catch (err) {
      console.error('Error fetching additional translations:', err);
      showToast(err.response?.data?.detail || t('termDetails.additionalTranslationsFailed', 'Failed to fetch additional translations'), 'error');
    } finally {
      setFetchingAdditional(false);
    }
  };

  const handleGenerateExplanation = async (forceRegenerate = false) => {
    if (!termId || generatingExplanation) return;

    try {
      setGeneratingExplanation(true);
      setStreamingExplanation(""); // Initialize streaming text
      const modelToUse = selectedExplanationModel || modelConfig.expert_model || modelConfig.available_explanation_models?.[0];
      
      const token = localStorage.getItem('access_token') || '';
      const response = await fetch(`${API_URL}/terms/${termId}/explain`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          model: modelToUse,
          force_regenerate: forceRegenerate,
          stream: true // Always use streaming
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to generate explanation' }));
        throw new Error(errorData.detail || 'Failed to generate explanation');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let fullExplanation = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.trim()) {
            try {
              const parsed = JSON.parse(line);
              if (parsed.error) {
                throw new Error(parsed.error);
              }
              if (parsed.chunk !== undefined) {
                fullExplanation += parsed.chunk;
                setStreamingExplanation(fullExplanation);
              }
              if (parsed.done) {
                // Streaming complete
                setGeneratingExplanation(false);
                showToast(
                  parsed.cached 
                    ? t('termDetails.explanationCached', 'Explanation loaded from cache')
                    : t('termDetails.explanationGenerated', 'Explanation generated successfully'),
                  'success'
                );
                // Refresh term data to show the final explanation
                await fetchTerm();
                setStreamingExplanation(null);
                return;
              }
            } catch (parseError) {
              console.error('Error parsing stream chunk:', line, parseError);
            }
          }
        }
      }
    } catch (err) {
      console.error('Error generating explanation:', err);
      showToast(err.message || t('termDetails.explanationFailed', 'Failed to generate explanation'), 'error');
      setStreamingExplanation(null);
      setGeneratingExplanation(false);
    }
  };

  const handleCopyPrompt = async (promptKey, promptText) => {
    if (!promptText) return;
    
    try {
      await navigator.clipboard.writeText(promptText);
      setCopiedPromptKey(promptKey);
      showToast(t('termDetails.promptCopied', 'Prompt copied to clipboard'), 'success');
      // Reset after 2 seconds
      setTimeout(() => setCopiedPromptKey(null), 2000);
    } catch (err) {
      console.error('Error copying prompt:', err);
      showToast(t('termDetails.promptCopyFailed', 'Failed to copy prompt'), 'error');
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-3 text-gray-600">{t('common.loading')}</span>
      </div>
    );
  }

  if (error || !term) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error || t('termDetails.notFound', 'Term not found')}</p>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
          >
            {t('common.back', 'Back')}
          </button>
        </div>
      </div>
    );
  }

  const discrepancy = term.discrepancy || {
    has_discrepancy: false,
    en_discrepancy: false,
    cn_discrepancy: false,
    en_similarity: 1.0,
    cn_similarity: 1.0,
    discrepancy_score: 0.0,
  };

  const expertDiscrepancy = term.expert_discrepancy || {
    has_discrepancy: false,
    en_discrepancy: false,
    cn_discrepancy: false,
    en_similarity: 1.0,
    cn_similarity: 1.0,
    discrepancy_score: 0.0,
  };

  const hasAnyDiscrepancy = discrepancy.has_discrepancy || expertDiscrepancy.has_discrepancy;
  const maxDiscrepancyScore = Math.max(discrepancy.discrepancy_score, expertDiscrepancy.discrepancy_score);

  // Determine which language groups to show based on source language
  const sourceLang = term.source_language;
  const shouldHideLanguageGroup = (sourceLang, targetLang) => {
    if (!sourceLang) return false;
    const langMap = { 'vi': 'vi', 'zh-cn': 'cn', 'zh-tw': 'cn', 'en': 'en' };
    return langMap[sourceLang] === targetLang;
  };

  const hideEn = shouldHideLanguageGroup(sourceLang, 'en');
  const hideCn = shouldHideLanguageGroup(sourceLang, 'cn');
  const hideVi = shouldHideLanguageGroup(sourceLang, 'vi');
  
  // Ensure translation_prompts exists (for backward compatibility)
  const translationPrompts = term.translation_prompts || {};

  return (
    <div className="p-4">
      {/* Header */}
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 text-gray-600 hover:text-gray-900 rounded-md hover:bg-gray-100"
            title={t('common.back', 'Back')}
          >
            <ArrowLeft className="h-6 w-6" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">{term.original_text}</h1>
            <p className="text-xs text-gray-500 mt-0.5">
              {t('termDetails.documentInfo', 'Document {{docId}}, Page {{pageNum}}', { docId: term.document_id, pageNum: term.page_number })}
              {term.source_language && ` • ${t('termDetails.sourceLanguage', 'Source')}: ${term.source_language}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {user?.role !== 'viewer' && (
            <button
              onClick={handleRetryTranslation}
              disabled={retrying}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              title={t('termDetails.retryTranslation', 'Retry Translation')}
            >
              {retrying ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('termDetails.retrying', 'Retrying...')}
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  {t('termDetails.retryTranslation', 'Retry Translation')}
                </>
              )}
            </button>
          )}
          {hasAnyDiscrepancy && (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-800">
              ⚠️ {t('termDetails.highDiscrepancy', 'High Discrepancy')} ({Math.round(maxDiscrepancyScore * 100)}%)
            </span>
          )}
          <span className={`px-3 py-1 rounded-full text-sm font-semibold ${term.status === 'reviewed'
            ? 'bg-green-100 text-green-800'
            : 'bg-yellow-100 text-yellow-800'
            }`}>
            {t(`documentStatus.status.${term.status}`, term.status)}
          </span>
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left Column - Main Translations */}
        <div className="lg:col-span-2 space-y-4">
          {/* Discrepancy Summary */}
          {hasAnyDiscrepancy && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
              <h3 className="text-base font-semibold text-orange-900 mb-1.5">{t('termDetails.discrepancySummary', 'Translation Discrepancy Summary')}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                {expertDiscrepancy.has_discrepancy && (
                  <div>
                    <p className="font-medium text-orange-800">{t('termDetails.expertVsExpert2', 'Expert vs Expert 2')}</p>
                    <ul className="mt-1 space-y-1 text-orange-700">
                      {expertDiscrepancy.en_discrepancy && (
                        <li>• EN: {Math.round(expertDiscrepancy.en_similarity * 100)}% {t('termDetails.similarity', 'similarity')}</li>
                      )}
                      {expertDiscrepancy.cn_discrepancy && (
                        <li>• CN: {Math.round(expertDiscrepancy.cn_similarity * 100)}% {t('termDetails.similarity', 'similarity')}</li>
                      )}
                    </ul>
                  </div>
                )}
                {discrepancy.has_discrepancy && (
                  <div>
                    <p className="font-medium text-orange-800">{t('termDetails.expertVsCustom', 'Expert vs Custom')}</p>
                    <ul className="mt-1 space-y-1 text-orange-700">
                      {discrepancy.en_discrepancy && (
                        <li>• EN: {Math.round(discrepancy.en_similarity * 100)}% {t('termDetails.similarity', 'similarity')}</li>
                      )}
                      {discrepancy.cn_discrepancy && (
                        <li>• CN: {Math.round(discrepancy.cn_similarity * 100)}% {t('termDetails.similarity', 'similarity')}</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* AI Explain Section */}
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
            <div className="flex items-center gap-2 flex-wrap mb-4">
              <h3 className="text-base font-semibold text-purple-900 flex items-center gap-2">
                <BrainCircuit className="h-5 w-5" />
                {t('termDetails.aiExplain', 'AI Explain')}
              </h3>
              {term.ai_explanation && term.model && (
                <span className="text-xs text-purple-600 bg-purple-100 px-2 py-0.5 rounded">
                  {term.model.split('/').pop()}
                </span>
              )}
              <div className="flex-1 flex justify-end items-center gap-2">
                <select
                  value={selectedExplanationModel || modelConfig.expert_model || ''}
                  onChange={(e) => setSelectedExplanationModel(e.target.value)}
                  disabled={generatingExplanation}
                  className="min-w-[200px] px-2 py-1 text-xs border border-purple-300 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {(availableExplanationModels.length > 0 ? availableExplanationModels : [
                    modelConfig.expert_model,
                    modelConfig.expert_2_model,
                    ...(modelConfig.available_explanation_models || [])
                  ].filter(Boolean)).map((model) => (
                    <option key={model} value={model}>
                      {model}
                    </option>
                  ))}
                </select>
                {term.ai_explanation_prompt && (
                  <button
                    onClick={() => handleCopyPrompt('ai_explanation', term.ai_explanation_prompt)}
                    className="p-1.5 text-purple-600 hover:text-purple-700 hover:bg-purple-100 rounded transition-colors"
                    title={t('termDetails.copyPrompt', 'Copy explanation prompt')}
                  >
                    {copiedPromptKey === 'ai_explanation' ? (
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </button>
                )}
                {term.ai_explanation ? (
                  <button
                    onClick={() => handleGenerateExplanation(true)}
                    disabled={generatingExplanation}
                    className="inline-flex items-center px-2 py-1 border border-purple-300 rounded-md shadow-sm text-xs font-medium text-purple-700 bg-white hover:bg-purple-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    title={t('termDetails.regenerateExplanation', 'Regenerate with selected model')}
                  >
                    {generatingExplanation ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <RefreshCw className="h-3 w-3" />
                    )}
                  </button>
                ) : (
                  <button
                    onClick={() => handleGenerateExplanation(false)}
                    disabled={generatingExplanation}
                    className="inline-flex items-center px-2 py-1 border border-transparent rounded-md shadow-sm text-xs font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {generatingExplanation ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <>
                        <BrainCircuit className="h-3 w-3 mr-1" />
                        {t('termDetails.generate', 'Generate')}
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
            {(streamingExplanation !== null || term.ai_explanation) ? (
              <div className="bg-white rounded-md p-3 border border-purple-200 max-h-96 overflow-y-auto prose prose-sm max-w-none">
                <ReactMarkdown
                  className="text-sm text-gray-700 leading-relaxed"
                  remarkPlugins={[remarkMath]}
                  rehypePlugins={[rehypeKatex, rehypeRaw]}
                  components={{
                    p: ({ node, children, ...props }) => <p className="mb-2 last:mb-0" {...props}>{children}</p>,
                    h1: ({ node, children, ...props }) => <h1 className="text-lg font-bold mb-2 mt-3 first:mt-0" {...props}>{children}</h1>,
                    h2: ({ node, children, ...props }) => <h2 className="text-base font-bold mb-2 mt-3 first:mt-0" {...props}>{children}</h2>,
                    h3: ({ node, children, ...props }) => <h3 className="text-sm font-bold mb-1 mt-2 first:mt-0" {...props}>{children}</h3>,
                    ul: ({ node, children, ...props }) => <ul className="list-disc list-inside mb-2 space-y-1" {...props}>{children}</ul>,
                    ol: ({ node, children, ...props }) => <ol className="list-decimal list-inside mb-2 space-y-1" {...props}>{children}</ol>,
                    li: ({ node, children, ...props }) => <li className="ml-2" {...props}>{children}</li>,
                    code: ({ node, inline, children, ...props }) =>
                      inline ? (
                        <code className="bg-gray-100 px-1 py-0.5 rounded text-xs font-mono" {...props}>{children}</code>
                      ) : (
                        <code className="block bg-gray-100 p-2 rounded text-xs font-mono overflow-x-auto mb-2" {...props}>{children}</code>
                      ),
                    pre: ({ node, children, ...props }) => <pre className="bg-gray-100 p-2 rounded text-xs font-mono overflow-x-auto mb-2" {...props}>{children}</pre>,
                    blockquote: ({ node, children, ...props }) => <blockquote className="border-l-4 border-gray-300 pl-3 italic my-2" {...props}>{children}</blockquote>,
                    strong: ({ node, children, ...props }) => <strong className="font-semibold" {...props}>{children}</strong>,
                    em: ({ node, children, ...props }) => <em className="italic" {...props}>{children}</em>,
                    table: ({ node, children, ...props }) => (
                      <div className="overflow-x-auto mb-2">
                        <table className="min-w-full border-collapse border border-gray-300 text-xs" {...props}>{children}</table>
                      </div>
                    ),
                    thead: ({ node, children, ...props }) => <thead className="bg-gray-100" {...props}>{children}</thead>,
                    tbody: ({ node, children, ...props }) => <tbody {...props}>{children}</tbody>,
                    tr: ({ node, children, ...props }) => <tr className="border-b border-gray-200" {...props}>{children}</tr>,
                    th: ({ node, children, ...props }) => <th className="border border-gray-300 px-2 py-1 text-left font-semibold" {...props}>{children}</th>,
                    td: ({ node, children, ...props }) => <td className="border border-gray-300 px-2 py-1" {...props}>{children}</td>,
                    hr: ({ node, ...props }) => <hr className="my-3 border-gray-300" {...props} />,
                  }}
                >
                  {streamingExplanation !== null ? streamingExplanation : term.ai_explanation || ''}
                </ReactMarkdown>
                {generatingExplanation && (
                  <span className="inline-block w-2 h-4 bg-purple-600 animate-pulse ml-1 align-middle" />
                )}
              </div>
            ) : (
              <div className="bg-white rounded-md p-4 border border-purple-200 text-center text-gray-500">
                <BrainCircuit className="h-8 w-8 mx-auto mb-2 text-purple-400" />
                <p className="text-sm">{t('termDetails.noExplanation', 'No explanation generated yet. Click "Generate Explanation" to get an AI-powered analysis of this term.')}</p>
              </div>
            )}
          </div>

          {/* Sentence Context */}
          {term.sentence && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">
                {t('termDetails.sentenceContext', 'Sentence Context')}
              </h3>
              <div className="space-y-2">
                <div>
                  <p className="text-xs text-gray-600 mb-1">{t('termDetails.originalSentence', 'Original Sentence')}</p>
                  <div className="bg-white rounded-md p-2 border border-gray-200 text-sm text-gray-800">
                    {term.sentence.original_text}
                  </div>
                </div>
                {term.sentence.translated_text && (
                  <div>
                    <p className="text-xs text-gray-600 mb-1">{t('termDetails.translatedSentence', 'Translated Sentence')}</p>
                    <div className="bg-white rounded-md p-2 border border-gray-200 text-sm text-gray-800">
                      {term.sentence.translated_text}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Parent Page Context */}
          {term.page && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">
                {t('termDetails.parentPage', 'Parent Page Context')}
                {term.page.page_number && ` (Page ${term.page.page_number})`}
              </h3>

              {/* OCR Text (Markdown) */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs text-gray-600">{t('termDetails.ocrText', 'OCR Extracted Text')}</p>
                  {term.page && term.page.id && (
                    <button
                      onClick={() => setShowPdfModal(true)}
                      className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 px-2 py-1 rounded hover:bg-blue-50 transition-colors"
                    >
                      <Eye className="h-3 w-3" />
                      {t('termDetails.viewOriginalPage', 'View Original PDF Page')}
                    </button>
                  )}
                </div>
                <div className="bg-white rounded-md p-3 border border-gray-200 max-h-64 overflow-y-auto prose prose-sm max-w-none">
                  <ReactMarkdown
                    className="text-sm text-gray-700 leading-relaxed"
                    remarkPlugins={[remarkMath]}
                    rehypePlugins={[rehypeKatex, rehypeRaw]}
                    components={{
                      p: ({ node, children, ...props }) => <p className="mb-2 last:mb-0" {...props}>{children}</p>,
                      h1: ({ node, children, ...props }) => <h1 className="text-lg font-bold mb-2 mt-3 first:mt-0" {...props}>{children}</h1>,
                      h2: ({ node, children, ...props }) => <h2 className="text-base font-bold mb-2 mt-3 first:mt-0" {...props}>{children}</h2>,
                      h3: ({ node, children, ...props }) => <h3 className="text-sm font-bold mb-1 mt-2 first:mt-0" {...props}>{children}</h3>,
                      ul: ({ node, children, ...props }) => <ul className="list-disc list-inside mb-2 space-y-1" {...props}>{children}</ul>,
                      ol: ({ node, children, ...props }) => <ol className="list-decimal list-inside mb-2 space-y-1" {...props}>{children}</ol>,
                      li: ({ node, children, ...props }) => <li className="ml-2" {...props}>{children}</li>,
                      code: ({ node, inline, children, ...props }) =>
                        inline ? (
                          <code className="bg-gray-100 px-1 py-0.5 rounded text-xs font-mono" {...props}>{children}</code>
                        ) : (
                          <code className="block bg-gray-100 p-2 rounded text-xs font-mono overflow-x-auto mb-2" {...props}>{children}</code>
                        ),
                      pre: ({ node, children, ...props }) => <pre className="bg-gray-100 p-2 rounded text-xs font-mono overflow-x-auto mb-2" {...props}>{children}</pre>,
                      blockquote: ({ node, children, ...props }) => <blockquote className="border-l-4 border-gray-300 pl-3 italic my-2" {...props}>{children}</blockquote>,
                      strong: ({ node, children, ...props }) => <strong className="font-semibold" {...props}>{children}</strong>,
                      em: ({ node, children, ...props }) => <em className="italic" {...props}>{children}</em>,
                      table: ({ node, children, ...props }) => (
                        <div className="overflow-x-auto mb-2">
                          <table className="min-w-full border-collapse border border-gray-300 text-xs" {...props}>{children}</table>
                        </div>
                      ),
                      thead: ({ node, children, ...props }) => <thead className="bg-gray-100" {...props}>{children}</thead>,
                      tbody: ({ node, children, ...props }) => <tbody {...props}>{children}</tbody>,
                      tr: ({ node, children, ...props }) => <tr className="border-b border-gray-200" {...props}>{children}</tr>,
                      th: ({ node, children, ...props }) => <th className="border border-gray-300 px-2 py-1 text-left font-semibold" {...props}>{children}</th>,
                      td: ({ node, children, ...props }) => <td className="border border-gray-300 px-2 py-1" {...props}>{children}</td>,
                      hr: ({ node, ...props }) => <hr className="my-3 border-gray-300" {...props} />,
                    }}
                  >
                    {term.page.text}
                  </ReactMarkdown>
                </div>
              </div>

              {term.page.detected_language && (
                <p className="text-xs text-gray-500 mt-2">
                  {t('termDetails.pageLanguage', 'Page Language')}: {term.page.detected_language}
                </p>
              )}
            </div>
          )}

          {/* Translation Variations */}
          <div className="space-y-4">
            {/* English Translations */}
            {!hideEn && (
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
                <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                  <h2 className="text-base font-semibold text-gray-900">{t('termDetails.englishTranslations', 'English Translations')}</h2>
                </div>
                <div className="p-4 space-y-3">
                  <TranslationCard
                    title={t('review.expertEn', 'Expert EN')}
                    model={modelConfig.expert_model || t('common.loading')}
                    translation={term.expert_translated_en}
                    hasDiscrepancy={expertDiscrepancy.en_discrepancy}
                    similarity={expertDiscrepancy.en_similarity}
                    prompt={translationPrompts.expert_en}
                    promptKey="expert_en"
                    copiedPromptKey={copiedPromptKey}
                    onCopyPrompt={handleCopyPrompt}
                    t={t}
                  />
                  <TranslationCard
                    title={t('review.expert2En', 'Expert 2 EN')}
                    model={modelConfig.expert_2_model || t('common.loading')}
                    translation={term.expert_2_translated_en}
                    hasDiscrepancy={expertDiscrepancy.en_discrepancy}
                    similarity={expertDiscrepancy.en_similarity}
                    prompt={translationPrompts.expert_2_en}
                    promptKey="expert_2_en"
                    copiedPromptKey={copiedPromptKey}
                    onCopyPrompt={handleCopyPrompt}
                    t={t}
                  />
                  <TranslationCard
                    title={t('review.customEn', 'Custom EN')}
                    model={modelConfig.custom_model || t('common.loading')}
                    translation={term.custom_translated_en}
                    hasDiscrepancy={discrepancy.en_discrepancy}
                    similarity={discrepancy.en_similarity}
                    prompt={translationPrompts.custom_en}
                    promptKey="custom_en"
                    copiedPromptKey={copiedPromptKey}
                    onCopyPrompt={handleCopyPrompt}
                    t={t}
                  />
                </div>
              </div>
            )}

            {/* Chinese Translations */}
            {!hideCn && (
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
                <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                  <h2 className="text-base font-semibold text-gray-900">{t('termDetails.chineseTranslations', 'Chinese Translations')}</h2>
                </div>
                <div className="p-4 space-y-3">
                  <TranslationCard
                    title={t('review.expertCn', 'Expert CN')}
                    model={modelConfig.expert_model || t('common.loading')}
                    translation={term.expert_translated_cn}
                    hasDiscrepancy={expertDiscrepancy.cn_discrepancy}
                    similarity={expertDiscrepancy.cn_similarity}
                    prompt={translationPrompts.expert_cn}
                    promptKey="expert_cn"
                    copiedPromptKey={copiedPromptKey}
                    onCopyPrompt={handleCopyPrompt}
                    t={t}
                  />
                  <TranslationCard
                    title={t('review.expert2Cn', 'Expert 2 CN')}
                    model={modelConfig.expert_2_model || t('common.loading')}
                    translation={term.expert_2_translated_cn}
                    hasDiscrepancy={expertDiscrepancy.cn_discrepancy}
                    similarity={expertDiscrepancy.cn_similarity}
                    prompt={translationPrompts.expert_2_cn}
                    promptKey="expert_2_cn"
                    copiedPromptKey={copiedPromptKey}
                    onCopyPrompt={handleCopyPrompt}
                    t={t}
                  />
                  <TranslationCard
                    title={t('review.customCn', 'Custom CN')}
                    model={modelConfig.custom_model || t('common.loading')}
                    translation={term.custom_translated_cn}
                    hasDiscrepancy={discrepancy.cn_discrepancy}
                    similarity={discrepancy.cn_similarity}
                    prompt={translationPrompts.custom_cn}
                    promptKey="custom_cn"
                    copiedPromptKey={copiedPromptKey}
                    onCopyPrompt={handleCopyPrompt}
                    t={t}
                  />
                </div>
              </div>
            )}

            {/* Vietnamese Translations */}
            {!hideVi && (
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
                <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                  <h2 className="text-base font-semibold text-gray-900">{t('termDetails.vietnameseTranslations', 'Vietnamese Translations')}</h2>
                </div>
                <div className="p-4 space-y-3">
                  <TranslationCard
                    title={t('review.expertVi', 'Expert VI')}
                    model={modelConfig.expert_model || t('common.loading')}
                    translation={term.expert_translated_vi}
                    hasDiscrepancy={false}
                    similarity={1.0}
                    prompt={translationPrompts.expert_vi}
                    promptKey="expert_vi"
                    copiedPromptKey={copiedPromptKey}
                    onCopyPrompt={handleCopyPrompt}
                    t={t}
                  />
                  <TranslationCard
                    title={t('review.expert2Vi', 'Expert 2 VI')}
                    model={modelConfig.expert_2_model || t('common.loading')}
                    translation={term.expert_2_translated_vi}
                    hasDiscrepancy={false}
                    similarity={1.0}
                    prompt={translationPrompts.expert_2_vi}
                    promptKey="expert_2_vi"
                    copiedPromptKey={copiedPromptKey}
                    onCopyPrompt={handleCopyPrompt}
                    t={t}
                  />
                  <TranslationCard
                    title={t('review.customVi', 'Custom VI')}
                    model={modelConfig.custom_model || t('common.loading')}
                    translation={term.custom_translated_vi}
                    hasDiscrepancy={false}
                    similarity={1.0}
                    prompt={translationPrompts.custom_vi}
                    promptKey="custom_vi"
                    copiedPromptKey={copiedPromptKey}
                    onCopyPrompt={handleCopyPrompt}
                    t={t}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Additional Translations */}
        <div className="lg:col-span-1">
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden sticky top-4">
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
              <div className="mb-2">
                <h2 className="text-base font-semibold text-gray-900">{t('termDetails.additionalTranslations', 'Additional Translations from Multiple Models')}</h2>
                <p className="text-xs text-gray-500 mt-1">{t('termDetails.additionalTranslationsDesc', 'Get translations from 10 popular OpenRouter models for comparison')}</p>
              </div>
              {user?.role !== 'viewer' && (
                <button
                  onClick={handleFetchAdditionalTranslations}
                  disabled={fetchingAdditional}
                  className="w-full inline-flex items-center justify-center px-3 py-1.5 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {fetchingAdditional ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {t('termDetails.fetching', 'Fetching...')}
                    </>
                  ) : (
                    <>
                      <BrainCircuit className="h-4 w-4 mr-2" />
                      {t('termDetails.fetchTranslations', 'Fetch Translations')}
                    </>
                  )}
                </button>
              )}
            </div>

            {term.additional_translations && Object.keys(term.additional_translations).length > 0 ? (
              <div className="p-4 max-h-[calc(100vh-300px)] overflow-y-auto">
                {/* Group by language */}
                {!hideEn && (
                  <div className="mb-4">
                    <h3 className="text-sm font-semibold text-gray-800 mb-2">{t('termDetails.englishTranslations', 'English Translations')}</h3>
                    <div className="space-y-2">
                      {Object.entries(term.additional_translations).map(([modelName, translations]) => (
                        translations.en && (
                          <div key={modelName} className="border rounded-lg p-2 bg-gray-50 border-gray-200">
                            <div className="flex items-start justify-between mb-1">
                              <h4 className="text-xs font-medium text-gray-900 truncate" title={modelName}>
                                {modelName.split('/').pop()}
                              </h4>
                            </div>
                            <p className="text-xs text-gray-500 mb-1 truncate" title={modelName}>{modelName}</p>
                            <p className="text-gray-800 text-sm">{translations.en}</p>
                          </div>
                        )
                      ))}
                    </div>
                  </div>
                )}

                {!hideCn && (
                  <div className="mb-4">
                    <h3 className="text-sm font-semibold text-gray-800 mb-2">{t('termDetails.chineseTranslations', 'Chinese Translations')}</h3>
                    <div className="space-y-2">
                      {Object.entries(term.additional_translations).map(([modelName, translations]) => (
                        translations.cn && (
                          <div key={modelName} className="border rounded-lg p-2 bg-gray-50 border-gray-200">
                            <div className="flex items-start justify-between mb-1">
                              <h4 className="text-xs font-medium text-gray-900 truncate" title={modelName}>
                                {modelName.split('/').pop()}
                              </h4>
                            </div>
                            <p className="text-xs text-gray-500 mb-1 truncate" title={modelName}>{modelName}</p>
                            <p className="text-gray-800 text-sm">{translations.cn}</p>
                          </div>
                        )
                      ))}
                    </div>
                  </div>
                )}

                {!hideVi && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-800 mb-2">{t('termDetails.vietnameseTranslations', 'Vietnamese Translations')}</h3>
                    <div className="space-y-2">
                      {Object.entries(term.additional_translations).map(([modelName, translations]) => (
                        translations.vi && (
                          <div key={modelName} className="border rounded-lg p-2 bg-gray-50 border-gray-200">
                            <div className="flex items-start justify-between mb-1">
                              <h4 className="text-xs font-medium text-gray-900 truncate" title={modelName}>
                                {modelName.split('/').pop()}
                              </h4>
                            </div>
                            <p className="text-xs text-gray-500 mb-1 truncate" title={modelName}>{modelName}</p>
                            <p className="text-gray-800 text-sm">{translations.vi}</p>
                          </div>
                        )
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="p-4 text-center text-gray-500">
                <BrainCircuit className="h-10 w-10 mx-auto mb-2 text-gray-400" />
                <p className="text-xs">{t('termDetails.noAdditionalTranslations', 'No additional translations yet. Click "Fetch Translations" to get translations from 10 popular models.')}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* PDF Page Image Modal */}
      {showPdfModal && term.page && term.page.id && (
        <div className="fixed inset-0 z-50 overflow-y-auto" onClick={() => setShowPdfModal(false)}>
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => setShowPdfModal(false)}></div>

            {/* Modal panel */}
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full" onClick={(e) => e.stopPropagation()}>
              {/* Header */}
              <div className="px-6 py-4 border-b flex items-center justify-between bg-gray-50">
                <h3 className="text-lg font-semibold text-gray-900">
                  {t('termDetails.originalPage', 'Original PDF Page')}
                  {term.page.page_number && ` - Page ${term.page.page_number}`}
                </h3>
                <button
                  onClick={() => setShowPdfModal(false)}
                  className="text-gray-400 hover:text-gray-500 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Content */}
              <div className="px-6 py-4">
                <div className="bg-white rounded-md border border-gray-200 p-4 flex justify-center">
                  <PageImage pageId={term.page.id} pageNumber={term.page.page_number} />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TranslationCard({ title, model, translation, hasDiscrepancy, similarity, prompt, promptKey, copiedPromptKey, onCopyPrompt, t }) {
  const isCopied = copiedPromptKey === promptKey;
  
  return (
    <div className={`border rounded-lg p-3 ${hasDiscrepancy ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}`}>
      <div className="flex items-start justify-between mb-1.5">
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
          <p className="text-xs text-gray-500 mt-0.5">{t('review.model')} {model}</p>
        </div>
        <div className="flex items-center gap-2">
          {hasDiscrepancy && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
              ⚠️ {Math.round(similarity * 100)}% {t('termDetails.similarity', 'similarity')}
            </span>
          )}
          {prompt && (
            <button
              onClick={() => onCopyPrompt(promptKey, prompt)}
              className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
              title={t('termDetails.copyPrompt', 'Copy translation prompt')}
            >
              {isCopied ? (
                <CheckCircle2 className="h-4 w-4 text-green-600" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </button>
          )}
        </div>
      </div>
      <p className={`text-sm text-gray-800 ${translation ? '' : 'text-gray-400 italic'}`}>
        {translation || t('termDetails.noTranslation', 'No translation available')}
      </p>
    </div>
  );
}



